import Ember from 'ember';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, set, get } from '@ember/object';
import { debounce, cancel, later } from '@ember/runloop';
import { localCopy } from 'tracked-toolbox';
import { task } from 'ember-concurrency';
import { isPresent, isEmpty } from '@ember/utils';
import { underscore, camelize } from '@ember/string';
import toLatLngArray, { toCoords, toLatLngObject } from 'garaje/helpers/resource-coords';
import eventsLogger from 'garaje/utils/decorators/events-logger';
import { htmlSafe } from '@ember/template';
import { FEATURE_ICON_MAP } from 'garaje/utils/map/map-icons';
import { GENERIC_RESOURCE_TYPES, NON_POINTS_OF_INTEREST } from 'garaje/utils/enums';
import { humanize } from 'ember-cli-string-helpers/helpers/humanize';
import urlBuilder from 'garaje/utils/url-builder';
import leaflet from 'leaflet';
import 'leaflet-draw';
import 'leaflet-path-drag';
import { guidFor } from '@ember/object/internals';
import { getAutoGenerationModalPosition } from 'garaje/utils/resource-map';
import { defer } from 'rsvp';
import { getNeighborhoodPolygons } from 'garaje/utils/neighborhood-polygons';
import { getFlattenedEmployees } from 'garaje/utils/resource-overview';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { AvailabilityStatus, ResourceType } from 'garaje/services/live-map';
import { getIdFromURN } from 'garaje/models/generic-resource';
import {
  DEFAULT_MARKER_SIZE_RATIO,
  getAvatarInnerSize,
  getAvatarPhotoSize,
  getAvatarMonogramFontSize,
  getAvatarMonogramVerticalOffset,
  getCustomMarkerSize,
  getLegacyLiveMapMarkerSize,
  getMarkerBorderWidth,
  getMarkerGlyphSize,
  getMarkerShellBorderWidth,
  MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE,
} from 'garaje/utils/map-marker-scale';
import { getCustomPoiGlyphHtml } from 'garaje/utils/map/custom-poi-icons';
import { v4 as uuid } from 'uuid';

/**
 * @param {String}                              rasterImageUrl
 * @param {Number}                              mapWidth
 * @param {Number}                              mapHeight
 * @param {Model<AreaMap>}                      areaMap
 * @param {Model<MapFloor>}                     mapFloor
 * @param {Model<MapFeature>}                   selectedFeature
 * @param {Array<Model<MapFeature>>}            mapFeatures
 * @param {Function}                            onSelectedFeatureChange
 * @param {Boolean}                             isSnappingEnabled
 * @param {Object}                              resourceOptions
 * @param {Array<Object>}                       availableResources
 * @param {Object}                              additionalDetails // additional information to pass that might change on case to case basis
 * @param {Boolean}                             viewOnly
 * @param {Boolean}                             sidebarIsOpen
 * @param {Boolean}                             isLoading
 * @param {Boolean}                             shouldResetMap
 * @param {Function}                            updateResourceOverview
 * @param {Function}                            updateEmployeeOverview
 * @param {String}                              selectedEmployeeEmail
 * @param {Boolean}                             shouldShowEmployeeForm
 * @param {Function}                            setEmployeeFormVisibility
 * @param {Boolean}                             showUnplacedParkingPanel
 * @param {Function}                            setShowUnplacedParkingPanel
 * @param {Boolean}                             isDraftMap
 * @param {Array}                               gqlEmployees
 * @param {Function}                            updateAssignmentsPanel
 * @param {Function}                            deleteAssignment
 * @param {Array}                               unplacedParking
 * @param {Function}                            onSelectUnplacedResource
 * @param {Model}                               selectedUnplacedResource
 * @param {Array}                               genericResources
 * @param {Function}                            setPlacingResource
 * @param {Function}                            setMapFeatures
 * @param {Object}                              placingResource
 * @param {Number}                              containerHeight
 * @param {Function}                            setLeafletMap
 * @param {Object}                              leafletMap
 * @param {Object}                              drawingArea
 * @param {Array}                               deletableFeatures
 * @param {Function}                            setDeletableFeatures
 * @param {Boolean}                             isVersionMap
 * @param {Function}                            drawArea
 * @param {Function}                            clearSearchState
 * @param {Function}                            onMapDimensionsLoaded
 * @param {String}                              selectedFeatureId
 */
@eventsLogger
export default class ResourceMap extends Component {
  @service flashMessages;
  @service state;
  @service store;
  @service featureFlags;
  @service featureConfig;
  @service metrics;
  @service ajax;
  @service abilities;
  @service impressions;
  @service liveMap;
  @service currentAdmin;
  @service router;
  @service mapSearch;

  crs = leaflet.CRS.Simple;

  @localCopy('args.mapWidth', 1200) mapWidth;
  @localCopy('args.mapHeight', 800) mapHeight;
  @localCopy('args.sidebarIsOpen', false) sidebarIsOpen;
  @localCopy('args.isSnappingEnabled', true) isSnappingEnabled;
  @localCopy('args.shouldResetMap', false) shouldResetMap;

  @tracked map;
  @tracked isReady = false;

  @tracked drawStarted = false;
  @tracked drawingArea;

  // Icon HTML cache — keyed by a string derived from the icon's determining state.
  // Avoids regenerating identical htmlSafe() strings for hundreds of features per render.
  _iconHtmlCache = new Map();

  _getCachedIconHtml(key, factory) {
    const cache = this._iconHtmlCache;
    // eslint-disable-next-line ember/use-ember-get-and-set
    let cached = cache.get(key);
    if (cached === undefined) {
      cached = factory();
      // eslint-disable-next-line ember/use-ember-get-and-set
      cache.set(key, cached);
    }
    return cached;
  }

  @tracked cursorX;
  @tracked cursorY;
  @tracked containerWidth;
  @tracked containerHeight = this.getContainerHeight();

  @tracked isInteraction = false;
  @tracked isOverArea = false;
  @tracked debouncedInteractionOff;

  @tracked polylineX = [];
  @tracked polylineY = [];
  @tracked zoomDelta = 0.5;
  @tracked originalZoomValue;
  @tracked hasOpenPopup = false;
  @tracked originalScrollWheelZoomHandler;
  @tracked currentZoom = null;
  @tracked baseZoom = null;

  @tracked bulkDeskSelectionBox = [];
  @tracked selectedFeatures = [];
  @tracked hasSeenBulkSelectionTooltip = false;
  @tracked markerSizeRatio = DEFAULT_MARKER_SIZE_RATIO;
  @tracked legacyMarkerSizing = true;

  // (ar) all of the following tracked properties are related to the new UI for desk auto-generation
  // they can potentially be refactored into a separate component
  @tracked canAutoPlaceDesks = false;
  @tracked shouldShowDeskArrangementModal = false;
  @tracked deskAutoGenerationModalPosition = {};
  @tracked deskAutoPlacementSelection = { boundingBox: {} };
  @tracked autoDeskPlacementBox = [];
  @tracked neighborhoodToView = null;
  @tracked pressedKeys = {};
  @tracked neighborhoodMarginLength = 0.0265;

  unplacedDesksCache = null;
  snapDistanceWithZoom = 0;
  deskAutoGenerationDropdownVerticalOffsetInPixels = 45;
  deskAutoGenerationDropdownHorizontalOffsetInPixels = 0;
  deskAutoGenerationModalVerticalOffsetInPixels = 50;
  deskAutoGenerationModalHorizontalOffsetInPixels = 60;
  get isMapSetupImprovementsEnabled() {
    return this.featureFlags.isEnabled('map-setup-improvements-web');
  }

  get isCustomizableMarkerSizeEnabled() {
    return this.featureFlags.isEnabled('customizable-marker-size');
  }

  get isImprovedLabelsEnabled() {
    return this.featureFlags.isEnabled('maps-improved-labels');
  }

  get deskArrangementModalPosition() {
    return {
      x: this.deskAutoGenerationModalPosition.x + this.deskAutoGenerationDropdownHorizontalOffsetInPixels,
      y: this.deskAutoGenerationModalPosition.y + this.deskAutoGenerationDropdownVerticalOffsetInPixels,
    };
  }

  get shouldRenderResourceForm() {
    if (this.args.viewOnly) {
      return false;
    }
    const { shouldShowEmployeeForm, showUnplacedParkingPanel, isDraftMap } = this.args;
    const showInDraftMap = isDraftMap && this.featureConfig.isEnabled('maps.drafts');
    const showInEditMap = this.isEditResourceOverviewEnabled;
    return (showInDraftMap || showInEditMap) && !shouldShowEmployeeForm && !showUnplacedParkingPanel;
  }

  get shouldCloseUnplacedParkingPanel() {
    // Auto-close the panel if there are no more unplaced parking resources
    const { showUnplacedParkingPanel, unplacedParking, setShowUnplacedParkingPanel } = this.args;
    if (showUnplacedParkingPanel && Array.isArray(unplacedParking) && unplacedParking.length === 0) {
      if (setShowUnplacedParkingPanel) {
        setShowUnplacedParkingPanel(false);
      }
      return true;
    }
    return false;
  }

  @action
  checkUnplacedParkingPanel() {
    // Auto-close the panel if there are no more unplaced parking resources
    const {
      showUnplacedParkingPanel,
      unplacedParking,
      setShowUnplacedParkingPanel,
      onSelectedFeatureChange,
      clearSearchState,
    } = this.args;
    if (showUnplacedParkingPanel && Array.isArray(unplacedParking) && unplacedParking.length === 0) {
      if (setShowUnplacedParkingPanel) {
        setShowUnplacedParkingPanel(false);
      }
      // Also close any open resource forms
      if (onSelectedFeatureChange) {
        onSelectedFeatureChange(null);
        clearSearchState();
      }
    }
  }

  get shouldShowTooltips() {
    if (!this.args.viewOnly) {
      return true;
    }

    return !this.hasOpenPopup;
  }

  get isDrawing() {
    return this.args.placingResource?.type === 'room';
  }

  get isLiveMapView() {
    return this.args.viewOnly && !this.args.isVersionMap;
  }

  isCurrentUserLiveMapFeature = (feature) => {
    if (!this.isLiveMapView || this.currentAdmin?.employee?.id == null) return false;

    if (feature.type === 'desk') {
      const deskState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.DESK);
      const isAssignedToCurrentUser =
        deskState?.assignedToProfileData != null
          ? getIdFromURN(deskState?.assignedToProfileData.id) === this.currentAdmin.employee.id
          : false;
      const isReservedForCurrentUser =
        deskState?.status.reservedFor != null
          ? getIdFromURN(deskState?.status.reservedFor) === this.currentAdmin.employee.id
          : false;
      return isAssignedToCurrentUser || isReservedForCurrentUser;
    }

    if (feature.type === 'parking') {
      const parkingResourceState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.PARKING);
      return (
        getIdFromURN(parkingResourceState?.assignedTo) === this.currentAdmin.employee.id ||
        getIdFromURN(parkingResourceState?.status.reservedFor) === this.currentAdmin.employee.id
      );
    }

    return false;
  };

  get bounds() {
    const { mapWidth, mapHeight } = this;
    return new leaflet.LatLngBounds(
      leaflet.CRS.Simple.unproject({ x: 0, y: 0 }, 0),
      leaflet.CRS.Simple.unproject({ x: mapWidth, y: -mapHeight }, 0),
    );
  }

  get isLandscape() {
    return this.mapWidth > this.mapHeight;
  }

  get mapDimensions() {
    return [this.mapWidth, this.mapHeight];
  }

  get floorMapFeatures() {
    return this.args.mapFeatures.filter((feature) => {
      if (!this.featureConfig.isEnabled('desks') && feature.type === 'desk') {
        return false;
      }
      if (this.shouldDisableFeature(feature, false) && this.isFeatureUneditable(feature)) {
        return false;
      }
      if (feature.mapFloor) {
        return get(feature.mapFloor, 'id') === this.args.mapFloor.id;
      }
    });
  }

  get deskFeaturesOnFloor() {
    return this.floorMapFeatures.filter((feature) => feature.type === 'desk' && !feature.isDeleted);
  }

  get maxZoom() {
    const delta = 2.15;
    if (this.isLandscape) {
      return Math.sqrt(this.containerHeight / this.mapHeight) * delta;
    }
    return Math.sqrt(this.containerWidth / this.mapWidth) * delta;
  }

  get minZoom() {
    const delta = 1.35; //this delta prevents images from getting cut off
    if (this.isLandscape) {
      return -Math.sqrt(this.mapWidth / this.containerWidth) * delta;
    }

    return -Math.sqrt(this.mapHeight / this.containerHeight) * delta;
  }

  get canPlaceResource() {
    return (
      this.args.placingResource &&
      !this.isOverArea &&
      !this.isInteraction &&
      !this.autoGenerateDesksTask.isRunning &&
      isPresent(this.cursorX) &&
      isPresent(this.cursorY)
    );
  }

  get enableAnimation() {
    return !Ember.testing;
  }

  get enableTrackpadZoom() {
    return Boolean(this.args.viewOnly);
  }

  get wheelPxPerZoomLevel() {
    if (!this.enableTrackpadZoom) {
      return undefined;
    }
    // More sensitive trackpad zoom while still using fractional zoom steps.
    return 2;
  }

  get wheelDebounceTime() {
    if (!this.enableTrackpadZoom) {
      return undefined;
    }
    // Keep updates frequent so the gesture feels immediate.
    return 16;
  }

  get zoomPanOptions() {
    return {
      animate: this.enableAnimation,
    };
  }

  get isPointOfInterest() {
    const { selectedFeature } = this.args;
    if (!selectedFeature) return false;
    return !NON_POINTS_OF_INTEREST.includes(selectedFeature.type);
  }

  get selectedFeatureType() {
    const { selectedFeature } = this.args;

    if (GENERIC_RESOURCE_TYPES.includes(selectedFeature?.type) && this.abilities.can('use generic resource')) {
      return 'generic-resource';
    }

    return this.isPointOfInterest ? 'point-of-interest' : selectedFeature?.type;
  }

  get placingResourceType() {
    const { placingResource } = this.args;
    return this.isPointOfInterest ? 'point-of-interest' : placingResource?.type;
  }

  get isEditResourceOverviewEnabled() {
    const { isDraftMap, viewOnly } = this.args;
    return !isDraftMap && !viewOnly;
  }

  get resourceOverviewEnabled() {
    return this.args.isDraftMap || this.isEditResourceOverviewEnabled;
  }

  @action
  handleSelectUnplacedResource(resource) {
    // Call the callback to notify the parent component
    if (this.args.onSelectUnplacedResource) {
      this.args.onSelectUnplacedResource(resource);
    }

    // Also set the placingResource so that clicking on the map will place this resource
    if (resource && this.args.setPlacingResource) {
      // Create a placing resource object from the unplaced resource
      const placingResource = {
        type: 'parking',
        name: resource.name,
        resourceTypeId: resource.resourceTypeId,
        floorId: resource.floorId,
        // Add any other properties needed for placing
      };
      this.args.setPlacingResource(placingResource);
    } else if (!resource && this.args.setPlacingResource) {
      // When deselecting, set a generic parking resource so clicking on map creates new parking spots
      const genericParkingResource = {
        type: 'parking',
        title: 'Parking',
        geometryType: 'Point',
        icon: 'parking',
        beta: true,
        disabled: false,
        tooltip: 'Parking',
      };
      this.args.setPlacingResource(genericParkingResource);
    }
  }

  getFeatureName = (feature) => {
    const { viewOnly, isVersionMap } = this.args;

    if (feature.type === 'delivery-area' && feature.externalId) {
      const deliveryArea = this.store.peekRecord('delivery-area', feature.externalId);
      if (deliveryArea) {
        return deliveryArea.name;
      }
    }

    if (!feature.externalId || feature.type !== 'desk') {
      return feature.name;
    }

    const desk = this.store.peekRecord('desk', feature.externalId);
    const deskName = desk?.name || feature.name;

    const isLiveMap = viewOnly && !isVersionMap;

    if (isLiveMap) {
      const deskState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.DESK);
      if (!deskState) {
        return deskName;
      }

      const hasOccupant = deskState.assignedTo || deskState.status?.reservedFor;
      if (!hasOccupant) {
        return deskName;
      }

      if (!this.state.currentLocation.employeeScheduleEnabled) {
        return 'Person';
      }

      const employeeId = getIdFromURN(deskState.status?.reservedFor) || getIdFromURN(deskState.assignedTo);
      if (employeeId) {
        const employees = this.store.peekAll('employee');
        const employee = employees.find((e) => e.id === employeeId);
        if (employee?.hideFromEmployeeSchedule) {
          return 'Person';
        }
      }

      return deskState.status?.reservedForProfileData?.name || deskState.assignedToProfileData?.name || deskName;
    }

    const assignment = this.getAssignmentForDeskFeature(feature);

    if (!assignment) {
      return deskName;
    }

    const { user, employee, reservation, desk: assignmentDesk } = assignment;

    if (
      viewOnly &&
      !isVersionMap &&
      (user?.hideFromEmployeeSchedule ||
        employee?.hideFromEmployeeSchedule ||
        !this.state.currentLocation.employeeScheduleEnabled ||
        ((assignmentDesk.assignedTo || reservation) && !user && !employee))
    ) {
      return 'Person';
    }

    return employee?.name || user?.fullName || deskName;
  };

  getContainerHeight() {
    if (this.args.containerHeight) {
      return this.args.containerHeight;
    }

    const { clientHeight } = document.querySelector('#resource-map-wrapper');
    return clientHeight;
  }

  @action
  setNeighborhoodToView(neighborhoodId) {
    const { viewOnly } = this.args;

    this.metrics.trackEvent('Set neighborhood focus mode', { neighborhoodId });

    if (!viewOnly) {
      this.neighborhoodToView = neighborhoodId;
    }

    if (neighborhoodId) {
      const alreadySelected = this.args.additionalDetails.selectedNeighborhoods.find(
        (neighborhood) => neighborhood.id === neighborhoodId,
      );
      if (!alreadySelected) {
        this.args.additionalDetails.selectedNeighborhoods.pushObject(
          this.store.peekRecord('neighborhood', neighborhoodId),
        );
      }
    }
  }

  @action
  isPOIFeature(feature) {
    return feature.type && !NON_POINTS_OF_INTEREST.includes(feature.type);
  }

  @action
  isFeatureUneditable(feature) {
    const resourceState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.PARKING);
    if (
      (resourceState?.status.availabilityStatus === AvailabilityStatus.OCCUPIED ||
        resourceState?.status.availabilityStatus === AvailabilityStatus.UNAVAILABLE) &&
      this.args.additionalDetails.isDelegatedBooking
    ) {
      return true;
    }

    if (this.abilities.can('edit maps') || !this.isEditResourceOverviewEnabled) {
      return false;
    }

    const neighborhood = this.getFeatureNeighborhood(feature);

    if (!neighborhood) {
      return true;
    }

    const mapAbility = this.abilities.abilityFor('map');

    if (mapAbility?.editableNeighborhoodIds.includes(neighborhood.id)) {
      return false;
    }

    return true;
  }

  @action
  shouldDisableFeature(feature, checkHasError = true) {
    let resource;

    if (checkHasError && feature.hasError) {
      return false;
    }

    if (feature.isNew && feature.type === 'desk') {
      resource = feature[feature.type];
    } else if (feature.externalId) {
      resource = this.store.peekRecord(feature.type, feature.externalId);
    }

    if (isPresent(resource?.enabled)) {
      return !resource?.enabled;
    }

    if (this.featureConfig.isEnabled('mapsPointOfInterest')) {
      return !feature.enabled;
    }

    return false;
  }

  @action
  async onInsert() {
    this.loadImageTask.perform();

    // Load map settings before rendering live-map overlays and icon sizing.
    await Promise.all([
      this.loadNeighborhoodMarginSetting(),
      this.isCustomizableMarkerSizeEnabled ? this.loadMarkerSizeRatio() : Promise.resolve(),
    ]);

    this.impressions.getImpressions.perform(IMPRESSION_NAMES.MAP.BULK_EDITING_TOOLTIP_DISMISSED).then((impression) => {
      this.hasSeenBulkSelectionTooltip = impression?.length;
    });
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);

    this.handleResize();
  }

  @action
  onDestroy() {
    this.teardownTrackpadZoom(this.args.leafletMap);
    if (this._zoomAnimHandler && this.args.leafletMap) {
      this.args.leafletMap.off('zoomanim', this._zoomAnimHandler);
    }
    if (this._zoomAnimRAF != null) {
      cancelAnimationFrame(this._zoomAnimRAF);
      this._zoomAnimRAF = null;
    }
    if (this._syncCurrentZoomHandler && this.args.leafletMap) {
      this.args.leafletMap.off('zoomend', this._syncCurrentZoomHandler);
    }
    this.args.setLeafletMap(null);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.keyDownHandler);
    window.removeEventListener('keyup', this.keyUpHandler);
  }

  @action
  handleResize() {
    const { clientWidth, clientHeight } = document.querySelector('#resource-map-wrapper');
    this.containerHeight = clientHeight;
    this.containerWidth = clientWidth;
  }

  get inputIsFocused() {
    return document.activeElement.tagName === 'INPUT';
  }

  @action
  keyDownHandler(event) {
    const { placingResource, selectedFeature, leafletMap } = this.args;
    if (!this.pressedKeys[event.key]) {
      this.pressedKeys = { ...this.pressedKeys, [event.key]: true };
    }

    if ((selectedFeature || placingResource) && event.key === 'Escape') {
      this.closeResourceForm();

      if (this.isDrawing && this.args.drawingArea) {
        this.args.drawingArea.disable();
        this.args.leafletMap.dragging.enable();
      }
    }

    if (this.selectedFeatures.length && event.key === 'Escape') {
      this.selectedFeatures = [];
    }

    if (leafletMap && this.featureFlags.isEnabled('bulk-desk-editing-web')) {
      if ((this.pressedKeys['Meta'] || this.pressedKeys['Control']) && this.pressedKeys['a'] && !this.inputIsFocused) {
        this.selectedFeatures = this.deskFeaturesOnFloor.filter((feature) => !this.isFeatureUneditable(feature));
        this.metrics.trackEvent('Selected all desks with hotkey', { numberOfDesks: this.selectedFeatures.length });
        event.preventDefault();
        return;
      }

      if (this.pressedKeys['Shift'] && this.pressedKeys['Backspace']) {
        this.metrics.trackEvent('Delete selected desks with hotkey', { numberOfDesks: this.selectedFeatures.length });
        return;
      }

      if (this.pressedKeys['Shift']) {
        leafletMap.dragging.disable();
        return;
      }
    }

    if (
      (this.pressedKeys['Meta'] || this.pressedKeys['Control']) &&
      placingResource?.type === 'desk' &&
      this.isMapSetupImprovementsEnabled
    ) {
      this.canAutoPlaceDesks = true;
      if (leafletMap) {
        leafletMap.dragging.disable();
      }
    }
  }

  get shouldDeleteSelectedDesks() {
    return this.pressedKeys['Shift'] && this.pressedKeys['Backspace'];
  }

  @action
  keyUpHandler(event) {
    const { leafletMap } = this.args;
    delete this.pressedKeys[event.key];

    if (event.key === 'Shift' && leafletMap && this.featureFlags.isEnabled('bulk-desk-editing-web')) {
      leafletMap.dragging.enable();
      return;
    }

    if ((event.key === 'Meta' || event.key === 'Control') && this.featureFlags.isEnabled('bulk-desk-editing-web')) {
      this.pressedKeys = {};
    }

    if ((event.key === 'Meta' || event.key === 'Control') && this.isMapSetupImprovementsEnabled) {
      if (!this.deskAutoPlacementSelection.boundingBox.point1) {
        this.canAutoPlaceDesks = false;
        if (leafletMap) {
          leafletMap.dragging.enable();
        }
      }
    }
  }

  @action
  onFloorUpdate() {
    // We need to remove existing polygons that is related to the prev floor
    this.isReady = false;
    this.hasOpenPopup = false;
    this.toggleTooltipPane(true);
    this.args.leafletMap.eachLayer((layer) => {
      layer.options.isArea && this.args.leafletMap.removeLayer(layer);
    });
    this.loadImageTask.perform();

    if (!this.sidebarIsOpen) {
      this.sidebarIsOpen = true;
    }
  }

  @action
  onLoadImage() {
    // Without the timeout, when image is cached LeafletMap will render the markers before the image is fit to the bounds
    // When the image is fit the markers will be out of place. The timeout gives time for the image to be fit after loading
    later(
      this,
      () => {
        if (!Ember.testing) {
          this.args.leafletMap.fitBounds(this.bounds, { padding: [1, 1] });
          this.originalZoomValue = this.args.leafletMap.getZoom();
          this.baseZoom = this.originalZoomValue;
          this.currentZoom = this.originalZoomValue;
        }
        this.isReady = true;
      },
      500,
    );
  }

  get canDrawAutoPlacementBox() {
    return (
      this.featureFlags.isEnabled('map-setup-improvements-web') &&
      this.canAutoPlaceDesks &&
      this.deskAutoPlacementSelection.boundingBox?.point1
    );
  }

  get canDrawBulkSelectionBox() {
    const { viewOnly, placingResource } = this.args;
    return (
      // TODO: clean up bulk desk editing flag
      this.pressedKeys['Shift'] && !viewOnly && !placingResource && this.featureFlags.isEnabled('bulk-desk-editing-web')
    );
  }

  drawAutoPlacementBox(ev) {
    const { lat: startLat, long: startLong } = this.deskAutoPlacementSelection.boundingBox.point1;

    const top = [startLat, startLong];
    const right = [startLat, ev.latlng.lng];
    const bottom = [ev.latlng.lat, ev.latlng.lng];
    const left = [ev.latlng.lat, startLong];
    this.autoDeskPlacementBox = [top, right, bottom, left, top];
  }

  drawBulkSelectionBox(ev) {
    const [startLat, startLong] = this.bulkDeskSelectionBox[0];
    const top = [startLat, startLong];
    const right = [startLat, ev.latlng.lng];
    const bottom = [ev.latlng.lat, ev.latlng.lng];
    const left = [ev.latlng.lat, startLong];
    this.bulkDeskSelectionBox = [top, right, bottom, left];
  }

  @action
  onMouseDown(ev) {
    if (this.canDrawBulkSelectionBox) {
      // prevent text selection while drawing the box
      ev.originalEvent.preventDefault();
      if (!this.hasSeenBulkSelectionTooltip) {
        this.impressions.postImpression.perform(IMPRESSION_NAMES.MAP.BULK_EDITING_TOOLTIP_DISMISSED);
        this.hasSeenBulkSelectionTooltip = true;
      }
      this.bulkDeskSelectionBox = [[ev.latlng.lat, ev.latlng.lng]];
    }

    if (this.canAutoPlaceDesks && this.featureFlags.isEnabled('map-setup-improvements-web')) {
      this.metrics.trackEvent('Automatic desk generation launched');
      this.deskAutoPlacementSelection.boundingBox.point1 = { lat: ev.latlng.lat, long: ev.latlng.lng };
    }
  }

  @action
  handleMouseMove(ev) {
    const { offsetX, offsetY } = ev.originalEvent;
    if (this.bulkDeskSelectionBox.length) {
      // prevent text selection while drawing the box
      ev.originalEvent.preventDefault();
      this.drawBulkSelectionBox(ev);
    }

    if (this.canDrawAutoPlacementBox) {
      this.drawAutoPlacementBox(ev);
    }
    if (!this.isInteraction) {
      this.cursorX = offsetX;
      this.cursorY = offsetY;
    }
  }

  @action
  selectDesksInNeighborhood(neighorhoodId) {
    const desks = this.args.additionalDetails.desks;
    const neighborhoodDesks = desks.filter((desk) => desk.belongsTo('neighborhood').id() === neighorhoodId);
    const selectedFeatures = this.deskFeaturesOnFloor.filter((feature) =>
      neighborhoodDesks.some((desk) => desk.id === feature.externalId || desk.id === feature.desk?.id),
    );
    this.metrics.trackEvent('Selected desks in neighborhood', { numberOfDesks: selectedFeatures.length });
    this.selectedFeatures = selectedFeatures;
  }

  autoGenerateDesks = (ev) => {
    // we want to save these coordinates to know where to display the confirmation popup
    this.deskAutoPlacementSelection.boundingBox.point2 = { lat: ev.latlng.lat, long: ev.latlng.lng };

    const boundingBoxCoordinatesInPixels = this.args.leafletMap.latLngToContainerPoint(
      getAutoGenerationModalPosition(this.deskAutoPlacementSelection.boundingBox),
    );
    // apply the offsets so the auto-generation modal is above and center
    this.deskAutoGenerationModalPosition = {
      x: boundingBoxCoordinatesInPixels.x - this.deskAutoGenerationModalHorizontalOffsetInPixels,
      y: boundingBoxCoordinatesInPixels.y - this.deskAutoGenerationModalVerticalOffsetInPixels,
    };
    this.canAutoPlaceDesks = false;
    this.autoGenerateDesksTask.perform();
    this.args.leafletMap.dragging.enable();
  };

  getBoxCornerCoordinates(box) {
    const topRight = box.reduce(
      (acc, point) => {
        // where x is the largest and y is the largest
        if (point[0] >= acc[0] && point[1] >= acc[1]) {
          acc = point;
        }
        return acc;
      },
      [-Infinity, -Infinity],
    );

    const bottomLeft = box.reduce(
      (acc, point) => {
        // where x is the smallest and y is the smallest
        if (point[0] <= acc[0] && point[1] <= acc[1]) {
          acc = point;
        }
        return acc;
      },
      [Infinity, Infinity],
    );

    return [topRight, bottomLeft];
  }

  handleBulkDeskSelection() {
    const [topRight, bottomLeft] = this.getBoxCornerCoordinates(this.bulkDeskSelectionBox);
    const bottomLeftCoords = toCoords({ lng: bottomLeft[1], lat: bottomLeft[0] }, this.mapDimensions);
    const topRightCoords = toCoords({ lng: topRight[1], lat: topRight[0] }, this.mapDimensions);

    const selectedDesks = this.deskFeaturesOnFloor.filter((feature) => {
      if (this.isFeatureUneditable(feature)) {
        return false;
      }

      const [xPos, yPos] = feature.geometry.coordinates;

      return (
        xPos >= bottomLeftCoords.xPos &&
        xPos <= topRightCoords.xPos &&
        yPos <= bottomLeftCoords.yPos &&
        yPos >= topRightCoords.yPos
      );
    });

    return selectedDesks;
  }

  @action
  onMouseUp(ev) {
    const { onSelectedFeatureChange, clearSearchState } = this.args;
    if (this.bulkDeskSelectionBox.length) {
      let deskFeaturesInBox = this.handleBulkDeskSelection();
      const featuresToUnselect = this.selectedFeatures.filter((feature) => deskFeaturesInBox.includes(feature));
      this.selectedFeatures = this.selectedFeatures.filter((feature) => !featuresToUnselect.includes(feature));
      deskFeaturesInBox = deskFeaturesInBox.filter((feature) => !featuresToUnselect.includes(feature));

      this.selectedFeatures = [...this.selectedFeatures, ...deskFeaturesInBox];
      this.metrics.trackEvent('Selected desks in bulk', {
        totalSelected: this.selectedFeatures.length,
        newSelected: deskFeaturesInBox.length,
        unselected: featuresToUnselect.length,
      });
      this.bulkDeskSelectionBox = [];
      onSelectedFeatureChange(null);
      clearSearchState();
    }

    if (this.canAutoPlaceDesks && this.featureFlags.isEnabled('map-setup-improvements-web')) {
      this.autoGenerateDesks(ev);
    }
  }

  @action
  onSidebarToggle(isOpen) {
    this.sidebarIsOpen = isOpen;
  }

  @action
  resetMapView() {
    if (this.args.viewOnly) return;

    if (this.isDrawing) {
      this.args.drawingArea.disable();
      this.args.leafletMap.dragging.enable();
    }

    if (this.resourceOverviewEnabled) {
      this.sidebarIsOpen = false;
    }

    this.args.leafletMap.setMaxBounds(this.bounds);
    this.args.setPlacingResource(null);
  }

  @action
  registerMap({ target: map }) {
    // since there is no option to disable pan animation
    // this overrides the fn to skip removing class from map pane
    if (Ember.testing) {
      map._onPanTransitionEnd = () => {
        map.fire('moveend');
      };
    }

    map.on('moveend', () => {
      map.setMaxBounds(this.args.viewOnly ? this.bounds : null);
    });

    this._syncCurrentZoomHandler = () => {
      const zoom = map.getZoom();
      this.currentZoom = zoom;
      if (this.baseZoom == null) {
        this.baseZoom = zoom;
      }
    };

    // Listen to Leaflet's `zoomanim` (fired at the START of a zoom animation
    // with the target zoom in the event payload) instead of `zoom` (which
    // only fires AFTER Leaflet's 250ms CSS transition completes). Resizing
    // markers up-front lets them ride the map pane's CSS transform alongside
    // the map image, so they finish at the new size without a snap at the end.
    //
    // For continuous-zoom gestures (pinch), `zoomanim` fires once per frame —
    // coalesce those fires via requestAnimationFrame so we do at most one
    // tracked write per frame regardless of fire rate.
    this._zoomAnimRAF = null;
    this._latestAnimZoom = null;
    this._zoomAnimHandler = (event) => {
      this._latestAnimZoom = event.zoom;
      if (this._zoomAnimRAF != null) return;
      this._zoomAnimRAF = requestAnimationFrame(() => {
        this._zoomAnimRAF = null;
        const zoom = this._latestAnimZoom;
        this._latestAnimZoom = null;
        if (zoom == null) return;
        this.currentZoom = zoom;
        if (this.baseZoom == null) {
          this.baseZoom = zoom;
        }
      });
    };
    map.on('zoomanim', this._zoomAnimHandler);
    // `zoomend` stays as a safety net: when zoomAnimation is disabled or
    // Leaflet skips the animation (`_nothingToAnimate`), `zoomanim` doesn't
    // fire — we still need `currentZoom` to settle on the final value.
    map.on('zoomend', this._syncCurrentZoomHandler);
    this._syncCurrentZoomHandler();

    this._registerDrawingHandlers(map);
    this.setupTrackpadZoom(map);
    this.args.setLeafletMap(map);
  }

  isTrackpadZoomEvent(event) {
    return Boolean(event && (event.ctrlKey || event.metaKey));
  }

  setupTrackpadZoom(map) {
    if (!this.enableTrackpadZoom || !map?.scrollWheelZoom) {
      return;
    }

    const scrollWheelZoom = map.scrollWheelZoom;
    if (this.originalScrollWheelZoomHandler || !scrollWheelZoom._onWheelScroll) {
      return;
    }

    const originalHandler = scrollWheelZoom._onWheelScroll;
    this.originalScrollWheelZoomHandler = originalHandler;

    scrollWheelZoom._onWheelScroll = (event) => {
      if (!this.isTrackpadZoomEvent(event)) {
        return;
      }
      return originalHandler.call(scrollWheelZoom, event);
    };
  }

  teardownTrackpadZoom(map) {
    if (!this.originalScrollWheelZoomHandler || !map?.scrollWheelZoom) {
      return;
    }

    map.scrollWheelZoom._onWheelScroll = this.originalScrollWheelZoomHandler;
    this.originalScrollWheelZoomHandler = null;
  }

  @action
  toggleInteractionOn() {
    cancel(this.debouncedInteractionOff);
    this.isInteraction = true;
  }

  @action
  toggleInteractionOff() {
    this.resetCursor();
    this.debouncedInteractionOff = debounce(this, this.turnOffInteraction, 250);
  }

  @action
  turnOffInteraction() {
    this.isInteraction = false;
    this.resetCursor();
  }

  @action
  resetCursor() {
    this.cursorX = null;
    this.cursorY = null;
  }

  generateDeskName() {
    const { desks } = this.args.additionalDetails;
    let deskNumber = desks.length + 1;
    while (desks.any((desk) => desk.name === `Desk ${deskNumber}`)) {
      deskNumber += 1;
    }
    return `Desk ${deskNumber}`;
  }

  generateResourceName(resourceTypeId) {
    const { genericResources } = this.args.additionalDetails;
    const resourcesOfType = genericResources.filter((r) => r.resourceTypeId === resourceTypeId);

    let resourceCount = resourcesOfType.length + 1;

    while (resourcesOfType.any((r) => r.name === `Parking ${resourceCount}`)) {
      resourceCount += 1;
    }

    return `Parking ${resourceCount}`;
  }

  @action
  getNeighborhoodColor(neighborhoodId) {
    const neighborhood = this.store.peekRecord('neighborhood', neighborhoodId);
    return neighborhood?.displayColor;
  }

  @action
  getFeatureNeighborhood(feature) {
    let desk = feature.desk;
    if (feature.externalId) {
      desk = this.store.peekRecord('desk', feature.externalId);
    }
    if (!desk) {
      return null;
    }
    const neighborhoodId = desk.belongsTo('neighborhood').id();
    if (neighborhoodId) {
      const neighborhood = this.store.peekRecord('neighborhood', neighborhoodId);
      return neighborhood;
    }
    return null;
  }

  getDeskTooltipDisplay = (feature) => {
    const desk = feature.externalId ? this.store.peekRecord('desk', feature.externalId) : feature.desk;
    const deskName = desk?.name || feature.name;
    const neighborhood = this.getNeighborhoodNameForDesk(feature);
    const showNeighborhood = this.featureConfig.isEnabled('desks.neighborhood') && Boolean(neighborhood);
    const SEPARATOR = ' • ';

    const deskState = feature.externalId
      ? this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.DESK)
      : null;

    const assigneeName = this._getDeskAssigneeName(feature, deskState);
    if (assigneeName) {
      return showNeighborhood ? `${assigneeName}${SEPARATOR}${neighborhood}` : `${assigneeName}${SEPARATOR}${deskName}`;
    }

    return showNeighborhood ? `${deskName}${SEPARATOR}${neighborhood}` : deskName;
  };

  _getDeskAssigneeName = (feature, deskState) => {
    const isLiveMap = this.args.viewOnly && !this.args.isVersionMap;

    if (isLiveMap && deskState) {
      const hasOccupant = deskState.assignedTo || deskState.status?.reservedFor;
      if (!hasOccupant) return null;

      if (!this.state.currentLocation.employeeScheduleEnabled) return 'Person';

      const employeeId = getIdFromURN(deskState.status?.reservedFor) || getIdFromURN(deskState.assignedTo);
      if (employeeId) {
        const employee = this.store.peekAll('employee').find((e) => e.id === employeeId);
        if (employee?.hideFromEmployeeSchedule) return 'Person';
      }

      return deskState.status?.reservedForProfileData?.name || deskState.assignedToProfileData?.name || null;
    }

    const assignment = this.getAssignmentForDeskFeature(feature);
    if (!assignment) return null;
    const { user, employee, reservation, desk: assignmentDesk } = assignment;
    if (
      this.args.viewOnly &&
      !this.args.isVersionMap &&
      (user?.hideFromEmployeeSchedule ||
        employee?.hideFromEmployeeSchedule ||
        !this.state.currentLocation.employeeScheduleEnabled ||
        ((assignmentDesk.assignedTo || reservation) && !user && !employee))
    ) {
      return 'Person';
    }
    return employee?.name || user?.fullName || null;
  };

  getNeighborhoodNameForDesk = (feature) => {
    let desk = feature.desk;
    if (feature.externalId) {
      desk = this.store.peekRecord('desk', feature.externalId);
    }
    const deskState = feature.externalId
      ? this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.DESK)
      : null;
    if (deskState) {
      // null means no active neighbourhood for this desk right now
      if (!deskState.activeNeighborhoodId) return null;
      const neighborhood = this.store.peekRecord('neighborhood', String(deskState.activeNeighborhoodId));
      return neighborhood?.name;
    }
    // no live data yet, fall back to static relationship
    const staticNeighborhoodId = desk?.belongsTo('neighborhood').id();
    if (!staticNeighborhoodId) return null;
    const neighborhood = this.store.peekRecord('neighborhood', staticNeighborhoodId);
    return neighborhood?.name;
  };

  async loadNeighborhoodMarginSetting() {
    const locationId = this.state.currentLocationEnabledDesks?.id || this.state.currentLocation?.id;
    if (!locationId) return;

    // First, try to get the desk-location from the store (it may already be loaded by the route)
    const deskLocation = this.store.peekRecord('desk-location', locationId);

    if (deskLocation) {
      const setting = deskLocation.neighborhoodSliderSetting;
      if (typeof setting === 'number') {
        this.neighborhoodMarginLength = setting;
        return;
      }
    }

    // If not in store or no setting, fall back to API fetch
    try {
      const response = await fetch(`/api/desk-locations/${locationId}`);
      if (!response.ok) return;

      const data = await response.json();
      const setting = data?.data?.attributes?.['neighborhood-slider-setting'];

      if (typeof setting === 'number') {
        this.neighborhoodMarginLength = setting;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch neighborhood margin setting:', error);
    }
  }

  async loadMarkerSizeRatio() {
    if (!this.isCustomizableMarkerSizeEnabled) return;

    const locationId = this.state.currentLocationEnabledDesks?.id || this.state.currentLocation?.id;
    if (!locationId) return;

    const deskLocation = this.store.peekRecord('desk-location', locationId);

    if (deskLocation) {
      const ratio = deskLocation.markerSizeRatio;
      if (typeof ratio === 'number') {
        this.markerSizeRatio = ratio;
      }
      if (typeof deskLocation.legacyMarkerSizing === 'boolean') {
        this.legacyMarkerSizing = deskLocation.legacyMarkerSizing;
      }
      if (typeof ratio === 'number') {
        return;
      }
    }

    try {
      const response = await fetch(`/api/desk-locations/${locationId}`);
      if (!response.ok) return;

      const data = await response.json();
      const ratio = data?.data?.attributes?.['marker-size-ratio'];
      const legacyMarkerSizing = data?.data?.attributes?.['legacy-marker-sizing'];

      if (typeof ratio === 'number') {
        this.markerSizeRatio = ratio;
      }
      if (typeof legacyMarkerSizing === 'boolean') {
        this.legacyMarkerSizing = legacyMarkerSizing;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch marker size ratio setting:', error);
    }
  }

  get neighborhoodPolygons() {
    // only include desks where the live map confirms a neighborhood is active.
    // if a desk has live state but no activeNeighborhoodId (e.g. it has an assigned
    // employee today instead of a neighbourhood), exclude it from the polygon so the
    // neighbourhood rectangle doesn't appear on days it shouldn't.
    const activeNeighborhoodIdMap = {};
    const deskFeaturesForPolygons = this.deskFeaturesOnFloor.filter((feature) => {
      const deskState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.DESK);
      if (deskState) {
        if (deskState.activeNeighborhoodId !== null && deskState.activeNeighborhoodId !== undefined) {
          activeNeighborhoodIdMap[feature.externalId] = String(deskState.activeNeighborhoodId);
        }
        return deskState.activeNeighborhoodId !== null;
      }
      // no live data yet, fall back to static relationship (handled inside getNeighborhoodPolygons)
      return true;
    });

    return getNeighborhoodPolygons(
      deskFeaturesForPolygons,
      this.mapDimensions,
      this.store,
      this.neighborhoodMarginLength,
      activeNeighborhoodIdMap,
    );
  }

  @action
  // finds the desks assigned to the given employeeId from the GQL response, and appends the assignedTo field for each of them
  getAssignedDesksForEmployee(employeeId) {
    return getFlattenedEmployees(this.args.gqlEmployees?.assigned)
      .filter((it) => parseInt(it.id) === parseInt(employeeId))
      .map((it) => {
        return it.assignedDesks.map((assignedDesk) => {
          return {
            ...assignedDesk,
            assignedTo: it.email,
          };
        });
      })
      .flat();
  }

  @action
  convertToLatLngArray(coordinates) {
    const coords = coordinates.map((coordsArray) => toLatLngObject(coordsArray, this.mapDimensions));
    return coords;
  }

  @action
  onClick({ latlng }) {
    if (!this._shouldHandleClick()) return;

    this._handleViewOnlyMode();
    this._validateSelectedFeature();

    const newFeature = this._createFeatureAtLocation(latlng);

    if (this._isPlacingUnplacedParking()) {
      this._handleUnplacedParkingPlacement();
    } else {
      this._selectNewFeature(newFeature);
    }

    this._addFeatureToMap(newFeature);
    this._logPlacementEvent();
  }

  _shouldHandleClick() {
    return this.canPlaceResource && !this.isDrawing && !this.canAutoPlaceDesks;
  }

  _handleViewOnlyMode() {
    const { viewOnly, selectedFeature, onSelectedFeatureChange, clearSearchState } = this.args;
    if (viewOnly && selectedFeature) {
      onSelectedFeatureChange(null);
      clearSearchState();
    }
  }

  _validateSelectedFeature() {
    const { selectedFeature } = this.args;
    if (selectedFeature) {
      this._validateFeature(selectedFeature);
    }
  }

  _createFeatureAtLocation(latlng) {
    const { xPos, yPos } = toCoords(latlng, this.mapDimensions);
    return this.createFeature(this.args.placingResource, [xPos, yPos]);
  }

  _isPlacingUnplacedParking() {
    const { placingResource, selectedUnplacedResource, unplacedParking } = this.args;
    return placingResource?.type === 'parking' && selectedUnplacedResource && Array.isArray(unplacedParking);
  }

  _handleUnplacedParkingPlacement() {
    const { selectedUnplacedResource, unplacedParking } = this.args;

    const remaining = unplacedParking.filter((p) => p.id !== selectedUnplacedResource.id);

    if (remaining.length > 0) {
      this._selectNextUnplacedParking(remaining[0]);
    } else {
      this._finishUnplacedParkingPlacement();
    }
  }

  _selectNextUnplacedParking(nextResource) {
    const { onSelectUnplacedResource, setPlacingResource } = this.args;

    if (onSelectUnplacedResource) {
      onSelectUnplacedResource(nextResource);
    }

    if (setPlacingResource) {
      const nextPlacingResource = this._createPlacingResourceFromUnplaced(nextResource);
      setPlacingResource(nextPlacingResource);
    }
  }

  _createPlacingResourceFromUnplaced(unplacedResource) {
    return {
      type: 'parking',
      name: unplacedResource.name,
      resourceTypeId: unplacedResource.resourceTypeId,
      floorId: unplacedResource.floorId,
    };
  }

  _finishUnplacedParkingPlacement() {
    const { onSelectUnplacedResource, setPlacingResource, setShowUnplacedParkingPanel } = this.args;

    if (onSelectUnplacedResource) {
      onSelectUnplacedResource(null);
    }

    if (setPlacingResource) {
      setPlacingResource(null);
    }

    if (setShowUnplacedParkingPanel) {
      setShowUnplacedParkingPanel(false);
    }
  }

  _selectNewFeature(newFeature) {
    const { onSelectedFeatureChange } = this.args;
    onSelectedFeatureChange(newFeature);
  }

  _logPlacementEvent() {
    const { isDraftMap } = this.args;

    this.logPlaced({
      resource_type: underscore(this.placingResourceType || 'unknown'),
      isDraft: isDraftMap,
    });

    this.metrics.trackEvent('Placed Resource', {
      resourceType: underscore(this.placingResourceType || 'unknown'),
    });
  }

  @action
  createFeature(resource, coordinates) {
    const { areaMap, mapFloor, updateResourceOverview, setMapFeatures } = this.args;

    // Create the feature with minimal attributes to ensure it's properly tracked as new
    const feature = this.store.createRecord('map-feature', {
      type: resource.type,
      geometry: {
        type: resource.geometryType ?? 'Point',
        coordinates,
      },
      mapFloor,
      areaMap,
    });

    // Ensure the feature is properly tracked as new by not setting any ID
    // The feature will get its ID from the server during save

    // Check if the feature already exists in the array to prevent duplicates
    const existingFeature = this.args.mapFeatures.find((f) => f.tempId === feature.tempId);
    if (!existingFeature) {
      setMapFeatures([...this.args.mapFeatures, feature]);
    }
    if (resource.type === 'desk') {
      this.createDeskFeature(feature, coordinates);
    }

    if (resource.type === 'room') {
      this.createRoomFeature(feature);
    }

    if (GENERIC_RESOURCE_TYPES.includes(resource.type) && this.abilities.can('use generic resource')) {
      this.createGenericResourceFeature(feature, coordinates);
    }

    if (this.isPOIFeature(feature)) {
      this.createPOIFeature(feature);
    }

    feature.tempId = guidFor(feature);
    if (updateResourceOverview) {
      updateResourceOverview('create', feature);
    }
    return feature;
  }

  createGenericResourceFeature(feature, coordinates) {
    const { selectedUnplacedResource, genericResources, mapFloor } = this.args;
    if (selectedUnplacedResource) {
      set(feature, 'externalId', selectedUnplacedResource.id);
      set(feature, 'name', selectedUnplacedResource.name);
    } else {
      const resourceTypeId = 2;
      const name = this.generateResourceName(resourceTypeId);

      const genericResource = this.store.createRecord('generic-resource', {
        id: `temp-${uuid()}`,
        name,
        resourceTypeId,
        relationId: this.state.currentLocation.id,
        floorId: mapFloor.id,
        geometry: {
          type: 'Point',
          coordinates,
        },
      });

      genericResources.pushObject(genericResource);

      set(feature, 'externalId', genericResource.id);
      set(feature, 'name', name);
    }
  }

  createRoomFeature(feature) {
    const { availableResources } = this.args;
    if (isPresent(availableResources.rooms) && isEmpty(feature.externalId)) {
      const room = availableResources.rooms[0];
      set(feature, 'name', room.displayName);
      set(feature, 'externalId', room.id);
    }
  }

  createPOIFeature(feature) {
    if (feature.type === 'custom') {
      return;
    }

    if (feature.type === 'first-aid-kit') {
      set(feature, 'name', 'First aid');
    } else if (feature.type === 'aed') {
      set(feature, 'name', 'AED');
    } else {
      set(feature, 'name', humanize([feature.type]));
    }
  }

  createDeskFeature(feature, coordinates) {
    const { mapFloor, availableResources, additionalDetails } = this.args;
    const { unplacedDesks } = availableResources;
    const { desks, hasMaxActiveDesks, updateActiveDesksCount, nextDeskToPlace, setNextDeskToPlace, draft } =
      additionalDetails;
    const [xPos, yPos] = coordinates;
    if (unplacedDesks.length) {
      const desk = nextDeskToPlace;
      set(feature, 'externalId', desk.id);
      set(feature, 'desk', desk);
      set(feature, 'name', desk.name);
      set(desk, 'xPos', xPos);
      set(desk, 'yPos', yPos);
      setNextDeskToPlace();
    } else {
      const desk = this.store.createRecord('desk', {
        reservations: [],
        enabled: !this.featureConfig.isEnabled('desks.allowEnabling') ? false : !hasMaxActiveDesks,
        name: this.generateDeskName(),
        assignedTo: null,
        floorId: mapFloor.id,
        xPos,
        yPos,
      });
      if (draft) {
        set(desk, 'draft', draft);
      }
      desks.pushObject(desk);
      set(feature, 'desk', desk);
      set(feature, 'name', desk.name);

      if (!hasMaxActiveDesks) {
        updateActiveDesksCount(1);
      }
    }
  }

  getIconForFeature = (feature) => {
    if (feature.type === 'desk') {
      return this.getIconForDeskFeature(feature);
    }
    if (feature.type === 'parking') {
      return this.getIconForParkingFeature(feature);
    } else {
      return this.getFeatureHtml(feature);
    }
  };

  @action
  isFeatureHighlighted(feature) {
    const highlightedIds = this.args.highlightedFeatureExternalIds;
    if (!highlightedIds || !highlightedIds.length) {
      return false;
    }
    return feature.type === 'desk' && highlightedIds.includes(feature.externalId);
  }

  @action
  isFeatureScheduled(feature) {
    if (feature.type !== 'desk' || !feature.externalId || !this.store.peekRecord('desk', feature.externalId)) {
      return false;
    } else {
      const desk = this.store.peekRecord('desk', feature.externalId);
      return desk.assignmentDetails?.length > 0 || desk.assignments?.length > 0;
    }
  }

  getMarkerSize(feature) {
    const isSelected = (this.args.viewOnly || !this.args.placingResource) && this.args.selectedFeature === feature;
    const isBulkSelected = this.selectedFeatures.includes(feature);

    if (!this.isLiveMapView) {
      if (isSelected) {
        return 60;
      }

      if (isBulkSelected) {
        return 37;
      }

      return 30;
    }

    if (!this.isCustomizableMarkerSizeEnabled) {
      return getLegacyLiveMapMarkerSize({
        currentZoom: this.currentZoom,
        baseZoom: this.baseZoom,
        isSelected,
        isBulkSelected,
      });
    }

    return getCustomMarkerSize({
      legacyMarkerSizing: this.legacyMarkerSizing,
      currentMapWidth: this.currentMapImageWidth,
      markerSizeRatio: this.markerSizeRatio,
      currentZoom: this.currentZoom,
      baseZoom: this.baseZoom,
      isSelected,
      isBulkSelected,
    });
  }

  get currentMapImageWidth() {
    if (
      !this.isLiveMapView ||
      !this.args.leafletMap ||
      !Number.isFinite(this.currentZoom) ||
      !Number.isFinite(this.mapWidth)
    ) {
      return null;
    }

    return this.mapWidth * this.args.leafletMap.getZoomScale(this.currentZoom, 0);
  }

  @action
  getMarkerIconSize(feature) {
    const size = this.getMarkerSize(feature);
    return [size, size];
  }

  shouldUseLiveMapCustomShell = (feature) => {
    return (
      this.isLiveMapView &&
      this.args.selectedFeature !== feature &&
      (feature.type === 'desk' || feature.type === 'parking' || feature.type in FEATURE_ICON_MAP)
    );
  };

  getFeatureHtml(feature) {
    const icon = FEATURE_ICON_MAP[feature.type];
    const labelColor = feature.enabled ? icon?.labelColor : 'carbon-30';
    const size = this.getMarkerSize(feature);
    if (this.shouldUseLiveMapCustomShell(feature) && feature.type in FEATURE_ICON_MAP) {
      return this.getCircularFeatureMarkerHtml(feature, size);
    }
    const cacheKey = `feature:${feature.type}:${labelColor}:${size}`;
    return this._getCachedIconHtml(cacheKey, () =>
      htmlSafe(`
      <div aria-hidden="true" data-test-feature-marker=${feature.type}>
        <div style="width:${size}px;height:${size}px;"></div>
        <!-- The feature label is being temporarily removed
        <div
          class="feature-label absolute text-center text-${labelColor}" data-test-feature-label>
        </div>
        -->
      </div>
    `),
    );
  }

  getIconForEditParkingFeature(feature) {
    if (!feature.externalId) {
      return this.getFeatureHtml(feature);
    }

    const resource = this.store.peekRecord('generic-resource', feature.externalId);

    const employees = this.store.peekAll('employee').slice();
    const employeeId = resource?.assignments?.[0]?.employeeId;

    if (!employeeId) {
      return this.getFeatureHtml(feature);
    }

    const employee = employees.find((employee) => employee.id === String(employeeId));

    if (!employee) {
      return this.getFeatureHtml(feature);
    }

    const profilePictureUrl = employee?.profilePictureUrl;

    if (profilePictureUrl && !profilePictureUrl.includes('gravatar.com')) {
      return this.getUserPhotoHtml(profilePictureUrl);
    }

    return this.getUserInitialsHtml(employee?.name);
  }

  getIconForParkingFeature(feature) {
    const isLiveMap = this.args.viewOnly && !this.args.isVersionMap;
    const markerSize = this.getMarkerSize(feature);
    if (!isLiveMap) {
      return this.getIconForEditParkingFeature(feature);
    }

    const parkingResourceState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.PARKING);
    if (!parkingResourceState) {
      return this.getDisabledParkingSpotHtml(markerSize);
    }

    if (this.args.additionalDetails.isDelegatedBooking) {
      if (
        parkingResourceState?.status.availabilityStatus === AvailabilityStatus.OCCUPIED ||
        parkingResourceState?.status.availabilityStatus === AvailabilityStatus.UNAVAILABLE
      ) {
        return this.getDisabledParkingSpotHtml(markerSize);
      }

      if (parkingResourceState?.status.availabilityStatus === AvailabilityStatus.AVAILABLE) {
        return this.getAvailableParkingSpotHtml(markerSize);
      }
    }

    if (this.currentAdmin == null || this.currentAdmin.employee == null) {
      return this.getDisabledParkingSpotHtml(markerSize);
    }

    const isAssignedToCurrentUser = getIdFromURN(parkingResourceState?.assignedTo) === this.currentAdmin.employee.id;

    const isReservedForCurrentUser =
      getIdFromURN(parkingResourceState?.status.reservedFor) === this.currentAdmin.employee.id;

    if (isAssignedToCurrentUser || isReservedForCurrentUser) {
      return this.currentUserParkingHtml(this.state.currentUser.userPhotoUrl, markerSize);
    }

    if (parkingResourceState?.status.availabilityStatus === AvailabilityStatus.OCCUPIED) {
      return this.getDisabledParkingSpotHtml(markerSize);
    }

    if (parkingResourceState?.status.availabilityStatus === AvailabilityStatus.AVAILABLE) {
      return this.getAvailableParkingSpotHtml(markerSize);
    }
    return this.getDisabledParkingSpotHtml(markerSize);
  }

  getLiveMapIconForDeskFeature(feature) {
    const markerSize = this.getMarkerSize(feature);
    const desk = this.store.peekRecord('desk', feature.externalId);
    const availableDesks = this.args.additionalDetails.availableDesks;
    const isAccessible = availableDesks === undefined ? true : !!(desk && availableDesks.includes(desk));

    if (desk && !desk.enabled) {
      return this.getDisabledDeskHtml(markerSize);
    }

    // It is important that we are checking states in this specific order:
    // 1. If the desk is assigned/reserved for the current user
    // 2. If the desk is available
    // 3. If the location has restricted sharing employee schedules
    // 4. If the desk is assigned/reserved for someone else
    // 5. Return the disabled desk by default

    // If a desk has no individual state but the live array is populated, the BE
    // intentionally omitted it — the desk is simply available. However, if the
    // entire live array is empty the BE returned no data at all (e.g. API error
    // for certain date ranges), so we show the desk as disabled/unavailable.
    const deskState = this.liveMap.getResourceStateByIdAndType(feature.externalId, ResourceType.DESK);
    if (!deskState) {
      const hasAnyLiveState = this.liveMap.resourceStates.length > 0;
      if (hasAnyLiveState && isAccessible) {
        return this.getAvailableDeskHtml(markerSize);
      }
      return this.getDisabledDeskHtml(markerSize);
    }

    // The current user's assignments and reservations are displayed with a red icon and a label
    const isAssignedToCurrentUser =
      deskState?.assignedToProfileData != null
        ? getIdFromURN(deskState?.assignedToProfileData.id) === this.currentAdmin?.employee?.id
        : false;
    const isReservedForCurrentUser =
      deskState?.status.reservedFor != null
        ? getIdFromURN(deskState?.status.reservedFor) === this.currentAdmin?.employee?.id
        : null;
    if (isAssignedToCurrentUser) {
      return this.currentUserHtml(this.isImprovedLabelsEnabled ? 'Your desk' : 'Assigned desk', markerSize);
    }
    if (isReservedForCurrentUser) {
      return this.currentUserHtml(
        this.isImprovedLabelsEnabled ? 'Your desk' : 'Reserved desk',
        markerSize,
        'reserved-desk-icon',
      );
    }

    // We should only show a desk as available if it is explicitly available
    if (deskState?.status.availabilityStatus === AvailabilityStatus.AVAILABLE) {
      if (!isAccessible) {
        return this.getDisabledDeskHtml(markerSize);
      }
      return this.getAvailableDeskHtml(markerSize);
    }

    // Show the desk as disabled if it's not available and we aren't showing employee schedules
    if (!this.state.currentLocation.employeeScheduleEnabled) {
      return this.getDisabledDeskHtml(markerSize);
    }
    // Other people's assignments and reservations are displayed with a user photo or initials
    if (deskState?.status?.reservedFor || deskState?.assignedTo) {
      const avatarUrl =
        deskState?.status?.reservedForProfileData?.avatarUrl || deskState?.assignedToProfileData?.avatarUrl;
      if (avatarUrl && !avatarUrl.includes('gravatar.com')) {
        return this.getUserPhotoHtml(avatarUrl, markerSize);
      }
      // Fallback to 'Person' when profile data is missing (e.g. stale assignment, employee deactivated)
      // to avoid rendering a blank monogram box (SQ-6755)
      const displayName =
        deskState?.status?.reservedForProfileData?.name || deskState?.assignedToProfileData?.name || 'Person';
      return this.getUserInitialsHtml(displayName, markerSize);
    }

    // Fall back to showing the desk as disabled
    return this.getDisabledDeskHtml(markerSize);
  }

  getIconForDeskFeature(feature) {
    const { viewOnly, isVersionMap } = this.args;
    const isLiveMap = viewOnly && !isVersionMap;
    const markerSize = this.getMarkerSize(feature);
    if (isLiveMap) {
      return this.getLiveMapIconForDeskFeature(feature);
    }

    const { assignedDesks } = this.args.additionalDetails;
    const assignment = this.getAssignmentForDeskFeature(feature);
    const desk = feature.externalId ? this.store.peekRecord('desk', feature.externalId) : feature.desk;
    if (!desk) {
      return this.getDisabledDeskHtml(markerSize);
    }

    if (!desk.enabled) {
      return this.getDisabledDeskHtml(markerSize);
    }

    if (assignment) {
      const { user, employee, reservation } = assignment;

      if (user?.id === this.state.currentUser.id && viewOnly) {
        let deskLabel = 'Your desk';
        if (user?.email === desk.assignedTo && assignedDesks?.some((assignedDesk) => assignedDesk.id === desk.id)) {
          deskLabel = 'Assigned desk';
        }
        return this.currentUserHtml(deskLabel, markerSize);
      }

      if (desk.enabled && !desk.assignedTo && !reservation) {
        return '';
      }

      if (viewOnly || desk.assignedTo) {
        if (user?.userPhotoUrl) {
          return this.getUserPhotoHtml(user.userPhotoUrl, markerSize);
        }

        if (user || employee) {
          return this.getUserInitialsHtml(employee?.name || user?.fullName, markerSize);
        }
      }
    }

    return '';
  }

  getAssignmentForDeskFeature(feature) {
    const { reservations } = this.args.additionalDetails;

    const desk = feature.externalId ? this.store.peekRecord('desk', feature.externalId) : feature.desk;
    const users = this.store.peekAll('user');
    const employees = this.store.peekAll('employee');

    let user;
    let employee;
    let employeeId;
    let reservation;
    const isLiveMap = this.args.viewOnly && !this.args.isVersionMap;

    if (!desk) {
      return undefined;
    }

    if (isLiveMap) {
      const deskState = this.liveMap.getResourceStateByIdAndType(desk.id, ResourceType.DESK);
      employeeId = getIdFromURN(deskState?.status?.reservedFor) || getIdFromURN(deskState?.assignedTo);
      employee = employees.find((employee) => employee.id === employeeId || employee.email === desk?.assignedTo);
      reservation = reservations?.find((res) => res.belongsTo('desk').id() == desk.id);
      user = users.find((user) => user.id === employee?.belongsTo('user')?.id() || user.email === desk?.assignedTo);

      if (!employee && reservation) {
        // if we have the reservation but we can't find the employee, we can try and derive the employee / user from the reservation
        employee = employees.find((employee) => employee.email === reservation?.userEmail);
        user = users.find((user) => user.email === reservation?.userEmail);
      }

      if (!user && !employee) {
        return undefined;
      }
      return { user, employee, reservation, desk };
    }
    // If we're not in the live map, we can't access the resource states
    else if (desk.assignedTo) {
      user = users.find((user) => user.email === desk.assignedTo);
      employee = employees.find((employee) => employee.email === desk.assignedTo);
    }

    // If an employee isn't found, try resolving it from the user relationship
    if (user && !employee) {
      employee = employees.find((employee) => employee.belongsTo('user').id() === user?.id);
    }

    // If we can't find the employeeId from the desk state or email from the desk assignment or reservations, return undefined
    if (desk.enabled && !desk.assignedTo && !reservation) {
      return undefined;
    }

    return {
      user,
      employee,
      reservation,
      desk,
    };
  }

  getUserInitialsHtml(userFullName, markerSize = 30) {
    const innerSize = getAvatarInnerSize(markerSize);
    const monogramInitials = this.monogramInitials(userFullName);
    const fontSize = getAvatarMonogramFontSize(markerSize, monogramInitials.length);
    const verticalOffset = getAvatarMonogramVerticalOffset(markerSize);
    const letterSpacing = monogramInitials.length > 1 ? `${(-fontSize * 0.04).toFixed(2)}px` : '0';

    return this.getCircularMarkerShellHtml({
      cacheKey: `initials:${userFullName}:${markerSize}`,
      markerSize,
      backgroundColor: 'hsl(224, 8%, 48%)',
      testSelector: 'data-test-monogram',
      innerHtml: `<div
        data-test-entry-monogram
        style="display:flex;align-items:center;justify-content:center;width:${innerSize}px;height:${innerSize}px;font-size:${fontSize}px;font-weight:600;letter-spacing:${letterSpacing};line-height:1;color:#FFFFFF;text-transform:uppercase;margin:0;transform:translateY(${verticalOffset}px);"
      >${monogramInitials}</div>`,
    });
  }

  monogramInitials(fullName) {
    let nameAttr = fullName ?? '';

    if (/\+\d+/.test(nameAttr)) {
      nameAttr = nameAttr.split('+')[0];
    }

    const nameArray = nameAttr.split(' ');
    let name = '';
    for (let i = 0; i < nameArray.length; i++) {
      name += nameArray[i].slice(0, 1);
    }
    return name.slice(0, 4); // max 4 chars
  }

  getUserPhotoHtml(src = '', markerSize = 30) {
    if (!src) {
      return;
    }
    const photoSize = getAvatarPhotoSize(markerSize);

    return this.getCircularMarkerShellHtml({
      cacheKey: `photo:${src}:${markerSize}`,
      markerSize,
      // Same dark-gray fill as the initials marker — only visible when the
      // shell hides its inner content at small sizes (markerSize < 14).
      // While the photo is rendered, it covers the bg fully.
      backgroundColor: 'hsl(224, 8%, 48%)',
      testSelector: 'data-test-profile-picture',
      contentAreaSize: photoSize,
      innerHtml: `<img
        alt=""
        class="block object-cover object-center rounded-full"
        style="width:${photoSize}px;height:${photoSize}px;"
        src="${src}"
        role="presentation"
        data-test-entry-feed-photo
        data-test-entry-thumbnail
      >`,
    });
  }

  getExistingDeskCoords() {
    const { lat: lat1, long: long1 } = this.deskAutoPlacementSelection.boundingBox.point1;
    const { xPos: x1, yPos: y1 } = toCoords({ lat: lat1, lng: long1 }, this.mapDimensions);
    const { lat: lat2, long: long2 } = this.deskAutoPlacementSelection.boundingBox.point2;
    const { xPos: x2, yPos: y2 } = toCoords({ lat: lat2, lng: long2 }, this.mapDimensions);

    const maxX = Math.max(x1, x2);
    const maxY = Math.max(y1, y2);
    const minX = Math.min(x1, x2);
    const minY = Math.min(y1, y2);

    const desksInBox = this.deskFeaturesOnFloor
      .filter((desk) => {
        const x = desk.geometry.coordinates[0];
        const y = desk.geometry.coordinates[1];
        if (x > minX && x < maxX && y > minY && y < maxY) {
          return true;
        }
        return false;
      })
      .map((desk) => ({ x: desk.geometry.coordinates[0], y: desk.geometry.coordinates[1] }));
    return desksInBox;
  }
  toggleDeskArrangementModal = (event) => {
    if (event) {
      event.stopPropagation();
    }
    this.shouldShowDeskArrangementModal = !this.shouldShowDeskArrangementModal;
  };
  autoGenerateDesksAndFeatures = async (placingResource) => {
    const { rasterImageUrl } = this.args;
    const url = urlBuilder.spacesAIApiUrl();
    const existingDeskCoords = this.getExistingDeskCoords();
    try {
      const response = await this.ajax.request(url, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: JSON.stringify({
          imageURL: rasterImageUrl,
          boundingBox: this.deskAutoPlacementSelection.boundingBox,
          existing: existingDeskCoords,
        }),
      });

      const autoGeneratedVerticalCoordinates = response.vertical_ordering;
      const autoGeneratedHorizontalCoordinates = response.horizontal_ordering;
      // the default order is vertical, so that is what gets initialized when a user creates a selection
      const generatedFeatures = autoGeneratedVerticalCoordinates.map((deskCoordinate) => {
        const feature = this.createFeature(placingResource, [deskCoordinate.x, deskCoordinate.y]);
        feature.tempId = guidFor(feature);
        return feature;
      });
      const autoGeneratedFeatures = generatedFeatures;
      const autoGeneratedDesks = generatedFeatures.map((it) => it.desk);
      this._addFeaturesToMap(autoGeneratedFeatures);

      this.deskAutoPlacementSelection.boundingBox = {};
      this.canAutoPlaceDesks = false;

      if (autoGeneratedFeatures.length) {
        this.flashMessages.showAndHideFlash('success', 'Desks generated successfully');
        this.metrics.trackEvent('Automatic desks placed', { numberOfDesks: autoGeneratedFeatures.length });
      } else {
        this.flashMessages.showAndHideFlash('warning', 'No desks detected', 'Try again or place desks manually');
        this.metrics.trackEvent('No desks detected [auto desk placement]');
      }

      return {
        autoGeneratedVerticalCoordinates,
        autoGeneratedHorizontalCoordinates,
        autoGeneratedFeatures,
        autoGeneratedDesks,
      };
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error generating desks');
      this.autoDeskPlacementBox = [];
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  @task({ drop: true }) autoGenerateDesksTask = {
    unplacedDesks: null,
    autoGeneratedVerticalCoordinates: null,
    autoGeneratedHorizontalCoordinates: null,
    numberAutoGeneratedDesksVertically: true,
    autoGeneratedDesks: [],
    autoGeneratedFeatures: [],
    taskCompletionResolver: null,
    placingResource: null,
    *perform() {
      const deferred = defer();
      const arrangeDesks = (existingDesks, coordinates, setMapFeatures, mapFeatures) => {
        this.autoGeneratedDesks.map((it) => it.deleteRecord());
        this.autoGeneratedFeatures.map((it) => {
          this.context.removeFeature(it);
        });
        setMapFeatures(mapFeatures.filter((it) => !this.autoGeneratedFeatures.includes(it)));
        existingDesks.removeObjects(this.autoGeneratedDesks);
        const generatedFeatures = coordinates.map((deskCoordinate) => {
          return this.context.createFeature(this.placingResource, [deskCoordinate.x, deskCoordinate.y]);
        });
        this.autoGeneratedFeatures = generatedFeatures;
        this.autoGeneratedDesks = generatedFeatures.map((it) => it.desk);
        this.context.canAutoPlaceDesks = false;
      };
      this.arrangeHorizontally = (event) => {
        const { setMapFeatures, additionalDetails, mapFeatures } = this.context.args;
        if (event) {
          event.stopPropagation();
        }
        this.context.metrics.trackEvent('Sort order changed', { direction: 'horizontal' });
        if (this.numberAutoGeneratedDesksVertically) {
          arrangeDesks(additionalDetails.desks, this.autoGeneratedHorizontalCoordinates, setMapFeatures, mapFeatures);
        }
        set(this, 'numberAutoGeneratedDesksVertically', false);
        this.context.shouldShowDeskArrangementModal = false;
      };
      this.arrangeVertically = (event) => {
        const { setMapFeatures, additionalDetails, mapFeatures } = this.context.args;
        if (event) {
          event.stopPropagation();
        }
        this.context.metrics.trackEvent('Sort order changed', { direction: 'vertical' });
        if (!this.numberAutoGeneratedDesksVertically) {
          arrangeDesks(additionalDetails.desks, this.autoGeneratedVerticalCoordinates, setMapFeatures, mapFeatures);
        }
        set(this, 'numberAutoGeneratedDesksVertically', true);
        this.context.shouldShowDeskArrangementModal = false;
      };
      this.abort = (event) => {
        if (event) {
          event.stopPropagation();
        }
        this.context.metrics.trackEvent('Automatic desk placement denied');
        const { setMapFeatures, mapFeatures } = this.context.args;
        const { desks, setNextDeskToPlace } = this.context.args.additionalDetails;

        const desksToRemove = this.autoGeneratedDesks.filter((it) => !this.unplacedDesks.includes(it));
        desksToRemove.map((it) => it.deleteRecord());
        this.autoGeneratedFeatures.map((it) => {
          this.context.removeFeature(it);
        });

        setMapFeatures(mapFeatures.filter((it) => !this.autoGeneratedFeatures.includes(it)));
        desks.removeObjects(desksToRemove);
        setNextDeskToPlace();

        this.context.autoDeskPlacementBox = [];
        deferred.resolve(false);
      };
      this.continue = (event) => {
        if (event) {
          event.stopPropagation();
        }

        this.context.metrics.trackEvent('Automatic desk placement confirmed');
        this.context.autoDeskPlacementBox = [];
        deferred.resolve(true);
      };
      const { placingResource, availableResources } = this.context.args;

      // store snapshots of these variables as the initial values of their state could be required in other functions
      this.unplacedDesks = availableResources.unplacedDesks;
      this.placingResource = placingResource;

      try {
        const {
          autoGeneratedVerticalCoordinates,
          autoGeneratedHorizontalCoordinates,
          autoGeneratedFeatures,
          autoGeneratedDesks,
        } = yield this.context.autoGenerateDesksAndFeatures(placingResource);

        // the desks, features, and coordinates are all generated by this point, we are storing them for use in other functions in this task
        this.autoGeneratedVerticalCoordinates = autoGeneratedVerticalCoordinates;
        this.autoGeneratedHorizontalCoordinates = autoGeneratedHorizontalCoordinates;
        this.autoGeneratedFeatures = autoGeneratedFeatures;
        this.autoGeneratedDesks = autoGeneratedDesks;

        if (autoGeneratedDesks.length === 0) {
          this.context.autoDeskPlacementBox = [];
          deferred.resolve(true);
        }
        yield deferred.promise;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('error', e);
        this.context.flashMessages.showAndHideFlash('error', 'Error generating desks');
      }
    },
  };

  // Returns the iconAnchor [x, y] for a Leaflet marker. For non-selected
  // markers we use Leaflet's default (centered: size/2). For selected
  // markers, anchor at the pin's "tip" which lives 2/3 from the top of the
  // 60 px red-pin SVG. Scaling by 2/3 of the current marker size keeps the
  // tip on the lat/lng regardless of how large or small the marker is —
  // replaces the previous hardcoded `margin-top: -40px` CSS hack which
  // didn't scale with marker size.
  @action
  getMarkerIconAnchor(feature) {
    const size = this.getMarkerSize(feature);
    const isSelected = (this.args.viewOnly || !this.args.placingResource) && this.args.selectedFeature === feature;
    if (isSelected) {
      return [size / 2, (size * 2) / 3];
    }
    return [size / 2, size / 2];
  }

  currentUserHtml(label, markerSize = 30, legacyClass = '') {
    const labelFontSize = this.isImprovedLabelsEnabled
      ? Math.max(16, Math.min(22, Math.round(markerSize * 0.73)))
      : Math.max(14, Math.min(18, Math.round(markerSize * 0.55)));
    const labelMargin = Math.max(8, Math.round(markerSize * 0.3));
    const containerWidth = Math.max(120, Math.round(markerSize * 4.5));

    if (this.isImprovedLabelsEnabled) {
      const borderWidth = getMarkerBorderWidth(markerSize);
      const avatarUrl = this.state.currentUser?.userPhotoUrl;
      const displayName = this.state.currentUser?.fullName;
      const labelStyle = `margin-left:${labelMargin}px;font-size:${labelFontSize}px;white-space:nowrap;pointer-events:none;-webkit-text-stroke:2.5px white;paint-order:stroke fill;`;

      if (avatarUrl && !avatarUrl.includes('gravatar.com')) {
        const photoSize = getAvatarPhotoSize(markerSize);
        return this._getCachedIconHtml(`current-user:${label}:${markerSize}:${avatarUrl}`, () =>
          htmlSafe(`
          <div aria-hidden="true" class="flex justify-start items-center" style="width:${containerWidth}px;pointer-events:none;" data-test-current-user>
            <div class="relative flex items-center justify-center rounded-full overflow-hidden shrink-0" style="width:${markerSize}px;height:${markerSize}px;box-sizing:border-box;border:${borderWidth}px solid var(--color-red-50, #E91C1C);box-shadow:0 5.4px 10.7px 0 rgba(0,0,0,0.12);pointer-events:auto;">
              <div class="flex items-center justify-center shrink-0" style="width:${photoSize}px;height:${photoSize}px;position:relative;z-index:1;">
                <img alt="" class="block object-cover object-center rounded-full" style="width:${photoSize}px;height:${photoSize}px;" src="${avatarUrl}" role="presentation" data-test-entry-feed-photo>
              </div>
            </div>
            <span class="text-red-50 reserved-desk-label" style="${labelStyle}">${label}</span>
          </div>
        `),
        );
      }

      const monogramInitials = this.monogramInitials(displayName);
      const innerSize = getAvatarInnerSize(markerSize);
      const fontSize = getAvatarMonogramFontSize(markerSize, monogramInitials.length);
      const verticalOffset = getAvatarMonogramVerticalOffset(markerSize);
      const letterSpacing = monogramInitials.length > 1 ? `${(-fontSize * 0.04).toFixed(2)}px` : '0';

      return this._getCachedIconHtml(`current-user:${label}:${markerSize}:${displayName}`, () =>
        htmlSafe(`
        <div aria-hidden="true" class="flex justify-start items-center" style="width:${containerWidth}px;pointer-events:none;" data-test-current-user>
          <div class="relative flex items-center justify-center rounded-full overflow-hidden shrink-0" style="width:${markerSize}px;height:${markerSize}px;box-sizing:border-box;border:${borderWidth}px solid var(--color-red-50, #E91C1C);background-color:hsl(224,8%,48%);box-shadow:0 5.4px 10.7px 0 rgba(0,0,0,0.12);pointer-events:auto;">
            <div class="flex items-center justify-center shrink-0" style="width:${innerSize}px;height:${innerSize}px;position:relative;z-index:1;">
              <div style="display:flex;align-items:center;justify-content:center;width:${innerSize}px;height:${innerSize}px;font-size:${fontSize}px;font-weight:600;letter-spacing:${letterSpacing};line-height:1;color:#FFFFFF;text-transform:uppercase;margin:0;transform:translateY(${verticalOffset}px);">${monogramInitials}</div>
            </div>
          </div>
          <span class="text-red-50 reserved-desk-label" style="${labelStyle}">${label}</span>
        </div>
      `),
      );
    }

    return this._getCachedIconHtml(`current-user:${label}:${markerSize}:${legacyClass}`, () => {
      // Render via the shared shell so the desk glyph hides automatically
      // when markerSize falls below MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE
      // — same behavior as every other shell-rendered marker. The previous
      // implementation used a single composite SVG (red-desk-icon.svg) that
      // baked icon + fill + border together, which made selective glyph
      // hiding impossible.
      const shellHtml = this.getCircularMarkerShellHtml({
        cacheKey: `current-user-shell:${markerSize}:${legacyClass}`,
        markerSize,
        backgroundColor: '#FA4338',
        contentAreaSize: markerSize,
        innerHtml: this.getDeskGlyphHtml(markerSize),
        legacySelectorClass: legacyClass,
      });

      return htmlSafe(`
      <div aria-hidden="true" class="flex justify-start items-center" style="width:${containerWidth}px;" data-test-current-user>
        ${shellHtml}
        <span class="text-red-40 reserved-desk-label" style="margin-left:${labelMargin}px;font-size:${labelFontSize}px;">${label}</span>
      </div>
    `);
    });
  }

  currentUserParkingHtml(src = '', markerSize = 30) {
    const photoSize = getAvatarPhotoSize(markerSize);
    const labelFontSize = Math.max(14, Math.min(18, Math.round(markerSize * 0.55)));
    const labelMargin = Math.max(8, Math.round(markerSize * 0.3));
    const containerWidth = Math.max(120, Math.round(markerSize * 4.5));

    return this._getCachedIconHtml(`current-user-parking:${src}:${markerSize}`, () => {
      // Both variants (no photo / with photo) now go through the shared
      // shell so the inner content hides automatically at small marker
      // sizes. The no-photo variant uses a red shell + parking glyph; the
      // photo variant uses a white shell with a red border + photo.
      const shellHtml = src
        ? this.getCircularMarkerShellHtml({
            cacheKey: `current-user-parking-photo:${src}:${markerSize}`,
            markerSize,
            backgroundColor: 'white',
            borderColor: 'var(--color-red-40, #FA4338)',
            contentAreaSize: photoSize,
            testSelector: 'data-test-profile-picture',
            innerHtml: `<img
              alt=""
              class="object-cover object-center rounded-full"
              style="width:${photoSize}px;height:${photoSize}px;"
              src="${src}"
              role="presentation"
              data-test-entry-feed-photo
              data-test-entry-thumbnail
            >`,
          })
        : this.getCircularMarkerShellHtml({
            cacheKey: `current-user-parking-shell:${markerSize}`,
            markerSize,
            backgroundColor: '#FA4338',
            contentAreaSize: markerSize,
            innerHtml: this.getParkingGlyphHtml(markerSize),
          });

      return htmlSafe(`
      <div aria-hidden="true" class="flex justify-start items-center" style="width:${containerWidth}px;" data-test-current-user>
        ${shellHtml}
        <span class="text-red-40 reserved-desk-label" style="margin-left:${labelMargin}px;font-size:${labelFontSize}px;">Your parking</span>
      </div>
    `);
    });
  }

  getDisabledDeskHtml(markerSize = 30) {
    return this.getDeskMarkerHtml({
      cacheKey: `disabled-desk:${markerSize}`,
      markerSize,
      backgroundColor: '#C9CBD4',
      testSelector: 'data-test-disabled-desk',
      legacySelectorClass: 'disabled-desk-icon',
    });
  }

  getAvailableDeskHtml(markerSize = 30) {
    return this.getDeskMarkerHtml({
      cacheKey: `available-desk:${markerSize}`,
      markerSize,
      backgroundColor: '#4EC176',
      testSelector: 'data-test-available-desk',
    });
  }

  getDisabledParkingSpotHtml(markerSize = 30) {
    return this.getParkingMarkerHtml({
      cacheKey: `disabled-parking:${markerSize}`,
      markerSize,
      backgroundColor: '#C9CBD4',
      testSelector: 'data-test-disabled-parking',
      legacySelectorClass: 'disabled-parking-icon',
    });
  }

  getAvailableParkingSpotHtml(markerSize = 30) {
    return this.getParkingMarkerHtml({
      cacheKey: `available-parking:${markerSize}`,
      markerSize,
      backgroundColor: '#4EC176',
      testSelector: 'data-test-available-parking',
    });
  }

  getCircularMarkerShellHtml({
    cacheKey,
    markerSize = 30,
    backgroundColor = '',
    borderColor = 'white',
    innerHtml = '',
    testSelector = '',
    contentAreaSize = null,
    legacySelectorClass = '',
  } = {}) {
    const borderWidth = getMarkerShellBorderWidth(markerSize);
    const innerSize = Math.max(markerSize - borderWidth * 2, 10);
    const resolvedContentAreaSize = contentAreaSize ?? innerSize;
    const hideContent = markerSize < MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE;
    const legacySelectorOverlay = legacySelectorClass
      ? `<div class="${legacySelectorClass}" style="position:absolute;inset:0;z-index:0;background-image:none;background-color:transparent;border:none;"></div>`
      : '';
    const innerContent = hideContent
      ? ''
      : `<div
          class="flex items-center justify-center shrink-0"
          style="width:${resolvedContentAreaSize}px;height:${resolvedContentAreaSize}px;position:relative;z-index:1;"
        >
          ${innerHtml}
        </div>`;

    return this._getCachedIconHtml(cacheKey, () =>
      htmlSafe(`<div
        aria-hidden="true"
        class="relative flex items-center justify-center rounded-full overflow-hidden shrink-0"
        style="width:${markerSize}px;height:${markerSize}px;box-sizing:border-box;border:${borderWidth}px solid ${borderColor};${
          backgroundColor ? `background-color:${backgroundColor};` : ''
        }box-shadow:0 5.4px 10.7px 0 rgba(0, 0, 0, 0.12);"
        ${testSelector}
      >
        ${legacySelectorOverlay}
        ${innerContent}
      </div>`),
    );
  }

  getDeskMarkerHtml({
    cacheKey,
    markerSize = 30,
    backgroundColor = '#4EC176',
    testSelector = '',
    legacySelectorClass = '',
  } = {}) {
    return this.getCircularMarkerShellHtml({
      cacheKey,
      markerSize,
      backgroundColor,
      contentAreaSize: markerSize,
      legacySelectorClass,
      testSelector,
      innerHtml: this.getDeskGlyphHtml(markerSize, backgroundColor),
    });
  }

  getParkingMarkerHtml({
    cacheKey,
    markerSize = 30,
    backgroundColor = '#4EC176',
    testSelector = '',
    legacySelectorClass = '',
  } = {}) {
    return this.getCircularMarkerShellHtml({
      cacheKey,
      markerSize,
      backgroundColor,
      contentAreaSize: markerSize,
      legacySelectorClass,
      testSelector,
      innerHtml: this.getParkingGlyphHtml(markerSize),
    });
  }

  getCircularFeatureMarkerHtml(feature, markerSize = 30) {
    const featureConfig = this.getCircularFeatureConfig(feature.type);
    if (!featureConfig) {
      return this.getFeatureHtml(feature);
    }

    const isDisabled = this.shouldDisableFeature(feature);
    const isCustom = feature.type === 'custom';
    // Customer-set color/icon only apply to custom POIs and only when not
    // disabled (the gray disabled treatment must keep winning).
    const customColor = isCustom && !isDisabled ? feature.displayColor : null;
    const customIcon = isCustom ? feature.displayIcon : null;

    const backgroundColor = isDisabled ? '#C9CBD4' : customColor || featureConfig.backgroundColor;

    return this.getCircularMarkerShellHtml({
      cacheKey: `feature-shell:${feature.type}:${backgroundColor}:${customIcon ?? ''}:${markerSize}`,
      markerSize,
      backgroundColor,
      contentAreaSize: markerSize,
      testSelector: `data-test-feature-marker=${feature.type}`,
      innerHtml: this.getCircularFeatureGlyphHtml(feature.type, markerSize, customIcon),
    });
  }

  getCircularFeatureConfig(featureType) {
    return {
      room: { backgroundColor: '#4EC176' },
      accessible: { backgroundColor: '#3F4450' },
      elevator: { backgroundColor: '#3F4450' },
      restroom: { backgroundColor: '#3F4450' },
      stairs: { backgroundColor: '#3F4450' },
      custom: { backgroundColor: '#3F4450' },
      aed: { backgroundColor: '#FF8F33' },
      'emergency-exit': { backgroundColor: '#FF8F33' },
      'fire-extinguisher': { backgroundColor: '#FF8F33' },
      'first-aid-kit': { backgroundColor: '#FF8F33' },
      printer: { backgroundColor: '#4E4EDA' },
      'break-room': { backgroundColor: '#4E4EDA' },
      cafe: { backgroundColor: '#4E4EDA' },
      kitchen: { backgroundColor: '#4E4EDA' },
      'mothers-room': { backgroundColor: '#4E4EDA' },
      'visitor-area': { backgroundColor: '#4E4EDA' },
      'delivery-area': { backgroundColor: '#4E4EDA' },
    }[featureType];
  }

  getCircularFeatureGlyphHtml(featureType, markerSize, displayIcon = null) {
    const glyphSize = getMarkerGlyphSize(markerSize, 'feature');

    if (featureType === 'custom') {
      const customGlyph = getCustomPoiGlyphHtml(displayIcon, glyphSize);
      if (customGlyph) return customGlyph;
    }

    const glyphMap = {
      room: `<svg class="shrink-0" viewBox="11 6 36 36" width="${glyphSize}" height="${glyphSize}" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M21.633 18.02 21 18l.52 6.26c.087.98.913 1.74 1.907 1.74h.906v3.333H23c-.367 0-.667.3-.667.667 0 .367.3.667.667.667h4c.367 0 .667-.3.667-.667 0-.367-.3-.667-.667-.667h-1.333V26h1.673a.986.986 0 0 0 .987-.993.996.996 0 0 0-.994-.994h-3.04a.701.701 0 0 1-.7-.626l-.446-3.974a1.575 1.575 0 0 0-1.514-1.393zm10.7 4.647h-6.666A.664.664 0 0 1 25 22c0-.367.3-.667.667-.667h6.666c.367 0 .667.3.667.667 0 .367-.3.667-.667.667zM30.667 26h1.666v3.333H31c-.367 0-.667.3-.667.667 0 .367.3.667.667.667h4c.367 0 .667-.3.667-.667 0-.367-.3-.667-.667-.667h-1.333V26h.913c.993 0 1.82-.76 1.907-1.74L37 18l-.633.02c-.78.027-1.427.62-1.514 1.393l-.446 3.974a.701.701 0 0 1-.7.626h-3.04a.996.996 0 0 0-.994.994c0 .546.447.993.994.993z" fill="#fff"/></svg>`,
      accessible: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M18.038 13.238c-.375 0-.697-.135-.966-.404a1.317 1.317 0 0 1-.403-.965c0-.375.134-.697.403-.966s.59-.403.966-.403c.375 0 .696.134.965.403s.403.59.403.966c0 .375-.134.697-.403.965-.269.27-.59.404-.965.404zM22.05 25.5v-3.919h-4.012c-.375 0-.694-.131-.957-.393a1.302 1.302 0 0 1-.393-.957V15.6c0-.375.13-.694.393-.956.263-.263.582-.394.957-.394.287 0 .53.056.73.169.2.112.438.331.713.656a8.283 8.283 0 0 0 1.725 1.538 3.884 3.884 0 0 0 2.044.656v1.125c-.637 0-1.294-.156-1.969-.469a8.752 8.752 0 0 1-1.95-1.256V20.1h2.494c.375 0 .694.131.956.394.263.262.394.581.394.956v4.05H22.05zm-5.625 0c-1.038 0-1.91-.353-2.616-1.06-.706-.706-1.059-1.578-1.059-2.615 0-.85.31-1.634.928-2.353.619-.719 1.434-1.135 2.447-1.247v1.144c-.675.062-1.216.34-1.622.834-.406.494-.61 1.035-.61 1.622 0 .725.241 1.331.723 1.819.48.487 1.084.731 1.809.731.587 0 1.131-.203 1.631-.61.5-.406.775-.946.825-1.621h1.144c-.1 1-.512 1.81-1.237 2.428-.726.619-1.513.928-2.363.928z" fill="#fff"/></svg>`,
      elevator: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M13.556 23.556h2.666v-3.778h.89v-2.445c0-.489-.175-.907-.523-1.255a1.712 1.712 0 0 0-1.256-.522h-.889c-.488 0-.907.174-1.255.522a1.711 1.711 0 0 0-.522 1.255v2.445h.889v3.778zm1.333-8.756c.34 0 .633-.122.878-.367.244-.244.366-.537.366-.877 0-.341-.122-.634-.366-.878a1.198 1.198 0 0 0-.878-.367c-.34 0-.633.122-.878.367a1.198 1.198 0 0 0-.367.878c0 .34.123.633.367.877.245.245.537.367.878.367zm3.889 2.089h4.444L21 13.333l-2.222 3.556zM21 22.667l2.222-3.556h-4.444L21 22.667zM11.333 26a1.28 1.28 0 0 1-.933-.4 1.28 1.28 0 0 1-.4-.933V11.333c0-.34.133-.648.4-.922s.578-.411.933-.411h13.334c.34 0 .648.137.922.411.274.274.411.582.411.922v13.334c0 .355-.137.666-.411.933-.274.267-.582.4-.922.4H11.333z" fill="#fff"/></svg>`,
      restroom: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M13 26v-6h-1v-4.56c0-.387.143-.723.43-1.01.287-.287.623-.43 1.01-.43h2.08c.387 0 .723.143 1.01.43.287.287.43.623.43 1.01V20h-1v6H13zm1.48-13.08c-.4 0-.743-.143-1.03-.43a1.405 1.405 0 0 1-.43-1.03c0-.4.143-.743.43-1.03.287-.287.63-.43 1.03-.43s.743.143 1.03.43c.287.287.43.63.43 1.03s-.143.743-.43 1.03c-.287.287-.63.43-1.03.43zM21.36 26v-4.8h-2.08L21 14.98c.093-.32.27-.563.53-.73.26-.167.563-.25.91-.25.347 0 .65.083.91.25.26.167.437.41.53.73l1.72 6.22h-2.08V26h-2.16zm1.08-13.08c-.4 0-.743-.143-1.03-.43a1.405 1.405 0 0 1-.43-1.03c0-.4.143-.743.43-1.03.287-.287.63-.43 1.03-.43s.743.143 1.03.43c.287.287.43.63.43 1.03s-.143.743-.43 1.03c-.287.287-.63.43-1.03.43z" fill="#fff"/></svg>`,
      stairs: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M10 26v-1.28h3.2v-3.84h3.84v-3.84h3.84V13.2h3.84V10H26v4.48h-3.84v3.84h-3.84v3.84h-3.84V26H10z" fill="#fff"/></svg>`,
      custom: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M24 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm-6 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm-8-2a2 2 0 1 0 4 0 2 2 0 0 0-4 0z" fill="#fff"/></svg>`,
      aed: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="m17.48 26.708.853.771.855-.77a94.212 94.212 0 0 0 3.645-3.49c.959-.98 1.723-1.855 2.292-2.625.57-.771.969-1.47 1.198-2.094a5.47 5.47 0 0 0 .344-1.896c0-1.25-.42-2.295-1.26-3.135-.841-.84-1.88-1.26-3.115-1.26-.75 0-1.452.176-2.105.53-.652.355-1.27.907-1.854 1.657-.5-.709-1.086-1.25-1.76-1.625a4.444 4.444 0 0 0-2.198-.563c-1.236 0-2.274.42-3.115 1.26-.84.84-1.26 1.886-1.26 3.136 0 .639.115 1.27.344 1.896.229.625.628 1.323 1.198 2.094.57.77 1.333 1.645 2.291 2.625.959.979 2.174 2.142 3.646 3.49zm-1.141-2.864c.007.087.038.16.093.219.063.065.137.1.224.103a.28.28 0 0 0 .223-.093l4.309-4.063a.403.403 0 0 0 .12-.459c-.06-.182-.18-.28-.36-.295l-3.363-.338 2.014-2.95a.33.33 0 0 0 .062-.229.346.346 0 0 0-.093-.218.317.317 0 0 0-.223-.104.28.28 0 0 0-.223.093l-4.31 4.063a.403.403 0 0 0-.119.459c.059.182.178.28.358.295l3.364.338-2.014 2.95a.329.329 0 0 0-.062.23z" fill="#fff"/></svg>`,
      'emergency-exit': `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M19.169 28v-5.28l-2.386-2.187-.928 4.153L10 23.471l.243-1.237 4.396.883 1.613-8.152-2.209 1.038v2.961h-1.326v-3.866l3.624-1.525c.471-.206.806-.335 1.005-.386.199-.052.401-.078.607-.078.295 0 .556.063.785.188.228.125.423.313.585.563l.928 1.48c.383.62.906 1.157 1.569 1.613.663.457 1.436.685 2.32.685v1.326c-.987 0-1.867-.228-2.64-.685-.774-.456-1.492-1.141-2.155-2.055l-.861 3.491 2.01 1.856V28H19.17zm.11-15.774c-.442 0-.821-.159-1.138-.475a1.552 1.552 0 0 1-.475-1.138c0-.442.159-.821.475-1.138A1.552 1.552 0 0 1 19.28 9c.442 0 .821.158 1.138.475.316.317.475.696.475 1.138 0 .442-.159.82-.475 1.138a1.553 1.553 0 0 1-1.138.475z" fill="#fff"/></svg>`,
      'fire-extinguisher': `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M15.284 26.258c-.479 0-.888-.17-1.228-.51-.34-.341-.511-.75-.511-1.229v-.869h8.693v.87c0 .478-.17.887-.511 1.227-.34.34-.75.511-1.228.511h-5.215zm-1.74-3.26v-4.78h8.694v4.78h-8.693zm.088-5.433c.145-.666.449-1.289.913-1.869a4.287 4.287 0 0 1 1.76-1.303 2.775 2.775 0 0 1-.695-.826l-3.804-.891v-.652l3.804-.891c.217-.42.525-.757.923-1.01.398-.254.851-.381 1.358-.381.305 0 .594.05.87.152.275.101.521.24.738.413l2.739-.565v5.216l-2.739-.566h-.021c.68.276 1.256.692 1.727 1.25.471.558.786 1.199.946 1.923h-8.52zm4.26-3.911c.376 0 .688-.127.934-.38.246-.254.37-.562.37-.924 0-.377-.128-.688-.381-.935a1.277 1.277 0 0 0-.924-.37c-.362 0-.67.124-.923.37-.254.247-.38.558-.38.935 0 .362.126.67.38.923.253.254.561.38.923.38z" fill="#fff"/></svg>`,
      'first-aid-kit': `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M17.475 21.253h1.05v-2.1h2.1v-1.05h-2.1v-2.1h-1.05v2.1h-2.1v1.05h2.1v2.1zM12.05 24c-.28 0-.525-.105-.735-.315A1.007 1.007 0 0 1 11 22.95v-9.1c0-.28.105-.525.315-.735.21-.21.455-.315.735-.315h3.15v-1.75c0-.28.105-.525.315-.735.21-.21.455-.315.735-.315h3.5c.28 0 .525.105.735.315.21.21.315.455.315.735v1.75h3.15c.28 0 .525.105.735.315.21.21.315.455.315.735v9.1c0 .28-.105.525-.315.735-.21.21-.455.315-.735.315h-11.9zm4.2-11.2h3.5v-1.75h-3.5v1.75z" fill="#fff"/></svg>`,
      printer: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M25.948 14.063h-2.323v-3.376A1.125 1.125 0 0 0 22.5 9.563h-9a1.125 1.125 0 0 0-1.125 1.124v3.376h-2.323A1.052 1.052 0 0 0 9 15.114v5.772a1.052 1.052 0 0 0 1.052 1.052h2.323v3.375a1.125 1.125 0 0 0 1.125 1.125h9a1.125 1.125 0 0 0 1.125-1.125v-3.375h2.323A1.052 1.052 0 0 0 27 20.886v-5.772a1.052 1.052 0 0 0-1.052-1.052zm-13.573 4.5a1.125 1.125 0 1 1 0-2.25 1.125 1.125 0 0 1 0 2.25zm9 5.625h-6.75v-2.25h6.75v2.25zm0-10.125h-6.75v-2.25h6.75v2.25z" fill="#fff"/></svg>`,
      'break-room': `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M12.47 25a.566.566 0 0 1-.582-.582v-.835h-.369a1.87 1.87 0 0 1-1.446-.631 2.163 2.163 0 0 1-.573-1.505v-4.7c0-.4.133-.747.398-1.038.265-.291.592-.437.98-.437.389 0 .716.146.981.437.266.291.398.637.398 1.039v3.359H23.83v-3.36c0-.4.133-.747.398-1.038.265-.291.592-.437.98-.437.39 0 .716.146.981.437.266.291.398.637.398 1.039v4.699c0 .582-.19 1.084-.572 1.505-.382.42-.858.63-1.428.63H24.2v.835a.566.566 0 0 1-.583.583.566.566 0 0 1-.582-.582v-.835h-9.98v.835a.566.566 0 0 1-.583.582zm.952-6.058v-2.194c0-.622-.171-1.175-.514-1.66a2.24 2.24 0 0 0-1.389-.923V13c0-.544.182-1.013.544-1.408a1.768 1.768 0 0 1 1.36-.592h9.242c.544 0 1 .197 1.369.592.369.395.553.864.553 1.408v1.165a2.362 2.362 0 0 0-1.398.942 2.74 2.74 0 0 0-.524 1.64v2.195h-9.243z" fill="#fff"/></svg>`,
      cafe: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M26 14.16h-3v-1c0-.55-.45-1-1-1H12c-.55 0-1 .45-1 1v6c0 3.31 2.69 6 6 6a5.99 5.99 0 0 0 5.65-4H24c1.65 0 3-1.35 3-3v-3c0-.55-.45-1-1-1zm-1 4c0 .55-.45 1-1 1h-1v-3h2v2z" fill="#fff"/></svg>`,
      kitchen: `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M14.5 26.75v-7.36a3.187 3.187 0 0 1-1.77-1.05c-.487-.553-.73-1.217-.73-1.99v-5.6h1.2v5.6h1.3v-5.6h1.2v5.6H17v-5.6h1.2v5.6c0 .773-.243 1.437-.73 1.99a3.187 3.187 0 0 1-1.77 1.05v7.36h-1.2zm8.3 0v-6.4h-2.3v-6.1c0-1.053.32-1.9.96-2.54.64-.64 1.487-.96 2.54-.96v16h-1.2z" fill="#fff"/></svg>`,
      'mothers-room': `<svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M18.272 26c-.575 0-1.132-.06-1.672-.18a8.354 8.354 0 0 1-1.467-.46c-.629-.267-1.141-.59-1.538-.97S13 23.587 13 23.12V18.5c0-.413.16-.793.482-1.14.321-.347.735-.653 1.241-.92a7.51 7.51 0 0 1 1.713-.62 8.24 8.24 0 0 1 1.897-.22c.643 0 1.276.073 1.898.22a7.511 7.511 0 0 1 1.713.62c.52.267.936.573 1.25.92.315.347.473.727.473 1.14v4.62c0 .227-.052.447-.154.66a2.13 2.13 0 0 1-.441.6 4.49 4.49 0 0 1-.667.53 5.306 5.306 0 0 1-.851.45c.014-.067.034-.253.061-.56 0-.773-.28-1.433-.84-1.98a2.803 2.803 0 0 0-2.031-.82 2.71 2.71 0 0 0-1.56.46c-.45.307-.793.7-1.025 1.18.438.107.838.18 1.2.22.362.04.68.06.954.06.232 0 .42-.007.564-.02.143-.013.236-.02.277-.02v2.08a10.636 10.636 0 0 1-.882.04zm2.523-4.4a1.6 1.6 0 0 0 1.159-.47c.321-.313.482-.69.482-1.13 0-.44-.16-.817-.482-1.13a1.6 1.6 0 0 0-1.16-.47 1.6 1.6 0 0 0-1.158.47c-.322.313-.482.69-.482 1.13 0 .44.16.817.482 1.13a1.6 1.6 0 0 0 1.159.47zm-2.462-6.8c.684 0 1.265-.23 1.744-.69.479-.46.718-1.03.718-1.71 0-.667-.24-1.233-.718-1.7a2.405 2.405 0 0 0-1.744-.7c-.697 0-1.282.233-1.753.7a2.302 2.302 0 0 0-.708 1.7c0 .68.236 1.25.707 1.71.472.46 1.057.69 1.754.69z" fill="#fff"/></svg>`,
      'visitor-area': `<svg viewBox="11 6 36 36" width="${glyphSize}" height="${glyphSize}" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M24.913 25.307c.04-.14.213-.78-.193-1.707-.84-1.93-.124-2.636.364-3.117.26-.257.455-.45.316-.73-.267-.526-.973-2.24.893-2.973 1.2-.467 1.854.613 2.094 1.133.253-.053.52-.093.826-.093 3.607 0 3.1 4.047 3.093 4.073 0 0-.373 3.44-3.093 3.44-2.093 0-2.813-2.18-3.013-3.066-.12.253-.22.706-.167 1.513.06.84-.58 1.527-1.1 1.553-.013 0-.026-.013-.02-.026zm1.727 1.36h4.72c1.64 0 2.973 1.313 2.973 2.926v1.274a.47.47 0 0 1-.473.466h-9.72a.47.47 0 0 1-.474-.466v-1.274c0-1.613 1.334-2.926 2.974-2.926z" fill="#fff"/></svg>`,
      'delivery-area': `<svg viewBox="11 6 36 36" width="${glyphSize}" height="${glyphSize}" xmlns="http://www.w3.org/2000/svg"><path d="m35.56 19.84-2.553-.967a.388.388 0 0 0-.287.007l-6.127 2.6v2.927l-1.48-.574.007-2.92 6.28-2.666-2.386-.914h-.027L22.44 19.8a.167.167 0 0 0-.107.153v7.74c0 .074.04.134.107.16l6.347 2.714a.65.65 0 0 0 .5.006c1.24-.486 6.38-2.766 6.38-2.766V20a.167.167 0 0 0-.107-.16z" fill="#fff"/></svg>`,
    };

    return glyphMap[featureType] || '';
  }

  getDeskGlyphHtml(markerSize) {
    const glyphSize = getMarkerGlyphSize(markerSize, 'desk');

    return `<svg
      aria-hidden="true"
      class="block shrink-0"
      width="${glyphSize}"
      height="${glyphSize}"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="scale(1.5)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.625 7.5h8.75a.625.625 0 1 1 0 1.25v6.875a.625.625 0 1 1-1.25 0V12.16a.285.285 0 0 0-.285-.285H9.16a.285.285 0 0 0-.285.285v3.465a.625.625 0 0 1-1.25 0V8.75a.625.625 0 0 1 0-1.25zm3.892 3.125h1.25a.625.625 0 0 0 0-1.25h-1.25a.625.625 0 1 0 0 1.25z" fill="#FFFFFF"/>
      </g>
    </svg>`;
  }

  getParkingGlyphHtml(markerSize) {
    const glyphSize = getMarkerGlyphSize(markerSize, 'parking');

    return `<svg
      aria-hidden="true"
      class="block shrink-0"
      width="${glyphSize}"
      height="${glyphSize}"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M25 15.5V14.5C25 14.22 24.84 14 24.65 14H23.94L23.41 11.57C23.34 11.24 23.06 11 22.73 11H13.23C12.9 11 12.62 11.24 12.55 11.57L12.02 14H11.35C11.16 14 11 14.22 11 14.5V15.5C11 15.78 11.16 16 11.35 16H11.58L11.36 17H10.7C10.31 17 10 17.36 10 17.8V22.5C10 22.78 10.22 23 10.5 23H12V24.3C12 24.68 12.32 25 12.7 25H13.3C13.68 25 14 24.68 14 24.3V23H22V24.3C22 24.69 22.32 25 22.7 25H23.29C23.68 25 23.99 24.68 23.99 24.3V23H25.49C25.77 23 25.99 22.78 25.99 22.5V17.8C26 17.36 25.69 17 25.3 17H24.6L24.38 16H24.65C24.84 16 25 15.78 25 15.5ZM12 21C11.45 21 11 20.55 11 20C11 19.45 11.45 19 12 19C12.55 19 13 19.45 13 20C13 20.55 12.55 21 12 21ZM25 20C25 20.55 24.55 21 24 21C23.45 21 23 20.55 23 20C23 19.45 23.45 19 24 19C24.55 19 25 19.45 25 20ZM22.87 17H13.14C13.05 17 12.99 16.92 13 16.84L13.82 12.36C13.84 12.27 13.91 12.21 14 12.21H21.97C22.06 12.21 22.14 12.28 22.15 12.36L23.01 16.84C23.02 16.93 22.95 17 22.87 17Z" fill="#FFFFFF"/>
    </svg>`;
  }

  @action
  cancelDraw() {
    if (this.isDrawing && this.args.drawingArea) {
      this.args.drawingArea.disable();
      this.args.leafletMap.dragging.enable();
    }
  }

  @action
  onResourceClick(resource, { target }) {
    if (this.isFeatureUneditable(resource)) {
      return;
    }

    const {
      selectedFeature,
      onSelectedFeatureChange,
      additionalDetails,
      isDraftMap,
      setPlacingResource,
      shouldShowEmployeeForm,
      setEmployeeFormVisibility,
      setShowUnplacedParkingPanel,
      showUnplacedParkingPanel,
      clearSearchState,
      selectedFeatureId,
    } = this.args;

    if (selectedFeature) this._validateFeature(selectedFeature);

    // Clear the search bar and reset selectedFeatureId when any resource is clicked
    if (selectedFeatureId != null) {
      clearSearchState();
    }

    onSelectedFeatureChange(resource);
    this.selectedFeatures = [];
    this.neighborhoodToView = null;

    // if the employee form is displayed right now, we want to hide it so the desk form can be displayed
    if (shouldShowEmployeeForm) {
      setEmployeeFormVisibility(false);
    }

    if (showUnplacedParkingPanel) {
      setShowUnplacedParkingPanel(false);
    }

    if (this.args.viewOnly) {
      if (['visitor-area', 'delivery-area'].includes(resource.type)) {
        target.openTooltip();
        target.off('mouseout');
        target.on('mouseout', () => target.openTooltip());
      } else {
        target.closeTooltip();
      }
      this.metrics.trackEvent('Element tapped', {
        element_identifier: 'point_of_interest',
        element_label: resource.type,
        filter_mode: additionalDetails.selectedResourceFilter,
      });
      this.metrics.trackEvent('Clicked Resource', {
        resourceType: resource.type,
      });
      return;
    }

    if (this.resourceOverviewEnabled) {
      setPlacingResource(null);
    }

    if (!this.sidebarIsOpen) {
      this.sidebarIsOpen = true;
    }

    if (!resource.isNew) {
      this.logEdited({
        resource_type: underscore(resource.type),
        isDraft: isDraftMap,
      });
    }
  }

  @action
  closeBulkEditForm() {
    this.selectedFeatures = [];
  }

  @action
  closeResourceForm() {
    const {
      onSelectedFeatureChange,
      selectedFeature,
      setPlacingResource,
      placingResource,
      setShowUnplacedParkingPanel,
      showUnplacedParkingPanel,
      clearSearchState,
    } = this.args;

    if (selectedFeature) {
      this._validateFeature(selectedFeature);
      onSelectedFeatureChange(null);
    }

    if (this.isDrawing && this.args.drawingArea) {
      this.args.drawingArea.disable();
      this.args.leafletMap.dragging.enable();
    }

    if (placingResource) {
      setPlacingResource(null);
    }

    if (showUnplacedParkingPanel) {
      setShowUnplacedParkingPanel(false);
    }

    if (this.featureConfig.isEnabled('maps.drafts') && this.resourceOverviewEnabled) {
      this.sidebarIsOpen = false;
    }

    clearSearchState();
  }

  @action
  openUnplacedParkingPanel() {
    const { setEmployeeFormVisibility, onSelectedFeatureChange, setShowUnplacedParkingPanel, clearSearchState } =
      this.args;
    setShowUnplacedParkingPanel(true);

    // Close any open resource or employee forms
    setEmployeeFormVisibility(false);
    if (onSelectedFeatureChange) {
      onSelectedFeatureChange(null);
      clearSearchState();
    }
  }

  @action
  closeUnplacedParkingPanel() {
    const { setShowUnplacedParkingPanel, setPlacingResource } = this.args;

    this.selectedUnplacedResource = null;
    setPlacingResource(null);
    setShowUnplacedParkingPanel(false);
  }

  @action
  onDragStart() {
    const { mapWidth, mapHeight, originalZoomValue, zoomDelta } = this;
    const zoomLevel = (this.args.leafletMap.getZoom() - originalZoomValue) / zoomDelta;
    const mapContainerHeight = 800;
    const mapContainerWidth = 958;
    const snapDistance = 10;
    const normalizedSnapValue = mapWidth > mapHeight ? mapWidth / mapContainerWidth : mapHeight / mapContainerHeight;
    const normalizedSnapDistance = normalizedSnapValue * snapDistance;
    this.snapDistanceWithZoom = normalizedSnapDistance * Math.pow(0.8, zoomLevel);
  }

  generateSnapping(target, feature) {
    const markerLatLng = target.getLatLng();

    const [closestXResource, closestYResource] = [
      this._findClosestResource(markerLatLng, 'lng', feature),
      this._findClosestResource(markerLatLng, 'lat', feature),
    ];

    const closestResources = {};
    const allAlignedResources = {};

    if (closestXResource) {
      closestResources.x = toLatLngObject(closestXResource.geometry.coordinates, this.mapDimensions);
      allAlignedResources.x = this._findAllAlignedResources(feature, closestXResource, 0);
    }

    if (closestYResource) {
      closestResources.y = toLatLngObject(closestYResource.geometry.coordinates, this.mapDimensions);
      allAlignedResources.y = this._findAllAlignedResources(feature, closestYResource, 1);
    }

    if (closestXResource) {
      if (closestYResource) {
        target.setLatLng({
          lat: closestResources.y.lat,
          lng: closestResources.x.lng,
        });
        this.polylineX = [[closestResources.y.lat, closestResources.x.lng]];
      } else {
        target.setLatLng({
          lat: markerLatLng.lat,
          lng: closestResources.x.lng,
        });
        this.polylineX = [[markerLatLng.lat, closestResources.x.lng]];
      }
      allAlignedResources.x.forEach((alignedResource) => {
        const alignedLatLng = toLatLngObject(alignedResource.geometry.coordinates, this.mapDimensions);
        this.polylineX.push([alignedLatLng.lat, alignedLatLng.lng]);
      });
    } else {
      this.polylineX = [];
    }

    if (closestYResource) {
      if (closestXResource) {
        target.setLatLng({
          lat: closestResources.y.lat,
          lng: closestResources.x.lng,
        });
        this.polylineY = [[closestResources.y.lat, closestResources.x.lng]];
      } else {
        target.setLatLng({ lat: closestResources.y.lat, lng: markerLatLng.lng });
        this.polylineY = [[closestResources.y.lat, markerLatLng.lng]];
      }
      allAlignedResources.y.forEach((alignedResource) => {
        const alignedLatLng = toLatLngObject(alignedResource.geometry.coordinates, this.mapDimensions);
        this.polylineY.push([alignedLatLng.lat, alignedLatLng.lng]);
      });
    } else {
      this.polylineY = [];
    }
  }

  bulkMoveSelectedFeatures(target, feature) {
    const { xPos: newXPos, yPos: newYPos } = toCoords(target.getLatLng(), this.mapDimensions);

    const xPosDiff = newXPos - (this.prevCoords ? this.prevCoords[0] : feature.geometry.coordinates[0]);
    const yPosDiff = newYPos - (this.prevCoords ? this.prevCoords[1] : feature.geometry.coordinates[1]);

    this.selectedFeatures.forEach((selectedFeature) => {
      if (selectedFeature === feature) return;

      const [xPos, yPos] = selectedFeature.geometry.coordinates;
      const geometry = { ...selectedFeature.geometry };
      geometry.coordinates = [xPos + xPosDiff, yPos + yPosDiff];
      set(selectedFeature, 'geometry', geometry);

      const desk = selectedFeature.desk
        ? selectedFeature.desk
        : this.store.peekRecord('desk', selectedFeature.externalId);
      set(desk, 'xPos', xPos + xPosDiff);
      set(desk, 'yPos', yPos + yPosDiff);
    });

    this.prevCoords = [newXPos, newYPos];
  }

  prevCoords = null;

  @action
  onDrag(feature, { target }) {
    if (this.selectedFeatures.length && this.selectedFeatures.includes(feature)) {
      this.bulkMoveSelectedFeatures(target, feature);
    }

    if (!this.isSnappingEnabled || this.selectedFeatures.length) return;
    this.generateSnapping(target, feature);
  }

  @action
  onDragEnd(feature, { target }) {
    this.resetCursor();
    this.polylineX = [];
    this.polylineY = [];

    const newLatLng = target.getLatLng();
    if (this.bounds.contains(newLatLng)) {
      const { xPos, yPos } = toCoords(newLatLng, this.mapDimensions);
      // this stinks but setting geometry properties directly does not make the model dirty
      const geometry = { ...feature.geometry };
      geometry.coordinates = [xPos, yPos];
      set(feature, 'geometry', geometry);
      if (feature.type === 'desk') {
        const desk = feature.desk ? feature.desk : this.store.peekRecord('desk', feature.externalId);
        set(desk, 'xPos', xPos);
        set(desk, 'yPos', yPos);
      }
    } else {
      target.setLatLng(toLatLngArray(feature.geometry.coordinates, this.mapDimensions));
    }

    if (this.prevCoords) {
      this.metrics.trackEvent('Bulk moved desks', { numberOfDesks: this.selectedFeatures.length });
    } else {
      this.onResourceClick(feature, { target });
    }

    this.prevCoords = null;
  }

  /**
   * @param {MapFeature} feature Map Feature
   * @param {Element} el  A leaflet popup
   */
  @action
  handlePopupAdded(feature, el) {
    this.hasOpenPopup = true;
    this.toggleTooltipPane(false);

    const popup = el.popup._contentNode.getBoundingClientRect();
    const leafletContainer = document.querySelector('.leaflet-container').getBoundingClientRect();
    const icon = document.querySelector(`.icon-for-feature-${feature.id}`).getBoundingClientRect();

    const CSS_MARGIN = 20; // match this exactly to what we use in the .leaflet-popup.left / .leaflet-popup.right css

    if (icon.left + icon.width + popup.width + CSS_MARGIN > leafletContainer.left + leafletContainer.width) {
      // NOTE: There seems to be an issue with ember leaflet, where you can set the `className` on the initial
      // popup render but when we determine direction and reset it, the changes are not applied to the element
      // This does not re-render in the template: `className={{if dm.popupToRight "right" "left"}}`
      // We can get around this by modifying the element directly.
      el.popup._container.classList.remove('right');
      el.popup._container.classList.add('left');
    } else {
      el.popup._container.classList.add('right');
      el.popup._container.classList.remove('left');
    }

    if (popup.top < leafletContainer.top) {
      el.popup._container.classList.add('bottom');
      el.popup._container.classList.remove('top');
    }

    if (popup.bottom > leafletContainer.bottom) {
      el.popup._container.classList.remove('bottom');
      el.popup._container.classList.add('top');
    }
  }

  @action
  handlePopupClosed() {
    this.hasOpenPopup = false;
    this.toggleTooltipPane(true);
  }

  toggleTooltipPane(show) {
    const pane = this.args.leafletMap?.getPanes()?.tooltipPane;
    if (pane) {
      pane.style.display = show ? '' : 'none';
    }
  }

  @action
  removeFeature(feature) {
    const {
      onSelectedFeatureChange,
      setMapFeatures,
      mapFeatures,
      deletableFeatures,
      setDeletableFeatures,
      isDraftMap,
      updateResourceOverview,
      clearSearchState,
    } = this.args;

    if (feature.geometry.type === 'Polygon') {
      this.args.leafletMap.eachLayer((layer) => {
        layer.options.isArea && layer.options.feature === feature && this.args.leafletMap.removeLayer(layer);
      });
    }

    if (!feature.isNew) {
      setDeletableFeatures([...deletableFeatures, feature]);
    }

    onSelectedFeatureChange(null);
    clearSearchState();
    setMapFeatures(mapFeatures.filter((f) => f !== feature));

    if (updateResourceOverview && this.resourceOverviewEnabled) {
      updateResourceOverview('delete', feature);
      this.sidebarIsOpen = false;
    } else {
      this.sidebarIsOpen = true;
    }
    this.logDeleted({
      resource_type: underscore(feature.type),
      isDraft: isDraftMap,
    });
  }

  @action
  removeAreas() {
    if (this.args.deletableFeatures.any((feature) => feature.geometry.type === 'Polygon')) {
      this.args.deletableFeatures
        .filter((feature) => feature.geometry.type === 'Polygon')
        .forEach((feature) => {
          this.args.leafletMap.eachLayer((layer) => {
            layer.options.isArea && layer.options.feature === feature && this.args.leafletMap.removeLayer(layer);
          });
        });
    }
  }

  @action
  checkAvailableResources() {
    const { resourceOptions, placingResource, setPlacingResource, drawingArea, leafletMap } = this.args;

    if (!placingResource) return;
    if (resourceOptions[camelize(this.placingResourceType)]?.disabled) {
      if (this.isDrawing && drawingArea) {
        drawingArea.disable();
        leafletMap.dragging.enable();
      }
      return setPlacingResource(null);
    }
  }

  _findClosestResource(marker, axis, draggingResource) {
    return this.floorMapFeatures
      .filter(({ geometry }) => geometry.type === 'Point')
      .find((feature) => {
        const featureLatLng = toLatLngObject(feature.geometry.coordinates, this.mapDimensions);
        return (
          featureLatLng[axis] - this.snapDistanceWithZoom < marker[axis] &&
          marker[axis] < featureLatLng[axis] + this.snapDistanceWithZoom &&
          feature !== draggingResource
        );
      });
  }

  _findAllAlignedResources(resource, closestResource, coordsIndex) {
    return this.floorMapFeatures.filter(
      (feature) =>
        feature.geometry.type === 'Point' &&
        feature.geometry.coordinates[coordsIndex] === closestResource.geometry.coordinates[coordsIndex] &&
        resource !== feature,
    );
  }

  _findClosestEditingPoint(currentLatLng, points, axis) {
    return points.find((point) => {
      return (
        point[axis] - this.snapDistanceWithZoom < currentLatLng[axis] &&
        currentLatLng[axis] < point[axis] + this.snapDistanceWithZoom &&
        currentLatLng.lat !== point.lat &&
        currentLatLng.lng !== point.lng
      );
    });
  }

  @action
  onEditMarkerDrag({ target, latlng: currentCoords }, area) {
    if (!currentCoords || !this.isSnappingEnabled) return;

    const [existingPoints] = area.getLatLngs();
    const [closestXPoint, closestYPoint] = [
      this._findClosestEditingPoint(currentCoords, existingPoints, 'lng'),
      this._findClosestEditingPoint(currentCoords, existingPoints, 'lat'),
    ];

    if (closestXPoint) {
      if (closestYPoint) {
        target.setLatLng({ lng: closestXPoint.lng, lat: closestYPoint.lat }).update();
      } else {
        target.setLatLng({ lng: closestXPoint.lng, lat: currentCoords.lat }).update();
      }
    }

    if (closestYPoint) {
      if (closestXPoint) {
        target.setLatLng({ lng: closestXPoint.lng, lat: closestYPoint.lat }).update();
      } else {
        target.setLatLng({ lng: currentCoords.lng, lat: closestYPoint.lat }).update();
      }
    }

    if (closestXPoint || closestYPoint) {
      target.fire('drag');
      target.fire('dragend');
    }
  }

  _registerDrawingHandlers(map) {
    leaflet.drawLocal.draw.handlers.polygon.tooltip = {
      start: null,
      cont: null,
      end: 'Click first point to close this shape',
    };

    const reset = () => {
      this.args.setPlacingResource(null);
      this.args.leafletMap.dragging.enable();
      leaflet.drawLocal.draw.handlers.polygon.tooltip.end = null;
    };

    map
      .on('draw:created', (e) => {
        const { layer } = e;
        const { onSelectedFeatureChange, placingResource, isDraftMap } = this.args;
        const [layerCoords] = layer.getLatLngs();
        const coordinates = layerCoords.map((latLng) => {
          const { xPos, yPos } = toCoords(latLng, this.mapDimensions);
          return [xPos, yPos];
        });

        const newFeature = this.createFeature(placingResource, coordinates);
        newFeature.tempId = guidFor(newFeature);
        onSelectedFeatureChange(newFeature);
        this._addFeatureToMap(newFeature);

        this.logPlaced({
          resource_type: underscore(this.placingResourceType || 'unknown'),
          isDraft: isDraftMap,
        });

        reset();
        this.args.drawArea(placingResource);
      })
      .on('draw:drawvertex', () => {
        this.drawStarted = true;
      })
      .on('draw:canceled', () => {
        reset();
        this.args.setPlacingResource(null);
      });
  }

  loadImageTask = task({ drop: true }, async () => {
    this.isReady = false;
    await new Promise((resolve) => {
      const image = new Image();
      image.src = this.args.rasterImageUrl;
      image.onerror = (_err) => {
        // In tests, proceed even if the image fails to load so markers render
        if (Ember.testing) {
          // Provide sensible defaults to unblock layout/calculations
          if (!this.mapHeight || !this.mapWidth) {
            this.mapHeight = 800;
            this.mapWidth = 1200;
          }

          // Expose map dimensions to parent if needed
          if (this.args.onMapDimensionsLoaded) {
            this.args.onMapDimensionsLoaded({ mapWidth: this.mapWidth, mapHeight: this.mapHeight });
          }

          resolve();
          return;
        }

        // Outside tests, keep previous behavior but don't reject to avoid stalling
        // eslint-disable-next-line no-console
        console.error('error loading map', _err);
        resolve();
      };
      image.onload = (ev) => {
        const { height: mapHeight, width: mapWidth } = ev.target;
        this.mapHeight = mapHeight;
        this.mapWidth = mapWidth;

        // Exposing the map dimensions to the parent component
        if (this.args.onMapDimensionsLoaded) {
          this.args.onMapDimensionsLoaded({ mapWidth, mapHeight });
        }

        resolve();
      };
    });
    // Ensure the map can render markers even if the image didn't trigger onLoad
    if (Ember.testing && !this.isReady) {
      this.isReady = true;
    }
  });

  @action
  _registerMarkerTestSelectors(feature, { target }) {
    // stank, cannot assign html attributes to <layer.marker /> directly
    target._icon.dataset.testMarker = true;
    target._icon.dataset.testFeatureType = feature.type;
    target._icon.dataset.testFeatureId = feature.id;
  }

  _validateFeature(feature) {
    if (feature.type !== 'desk') {
      const isValid = feature.requiredFields.every((field) => isPresent(feature[field]) && !feature.hasError);
      feature.hasError = !isValid;
    }
  }

  // Add this helper method
  _addFeaturesToMap(newFeatures) {
    const { setMapFeatures, mapFeatures } = this.args;
    const existingTempIds = new Set(mapFeatures.map((f) => f.tempId));
    const deduped = newFeatures.filter((f) => !existingTempIds.has(f.tempId));
    if (deduped.length > 0) {
      setMapFeatures([...mapFeatures, ...deduped]);
    }
  }

  // Update _addFeatureToMap to prevent duplicates
  _addFeatureToMap(newFeature) {
    const { setMapFeatures, mapFeatures } = this.args;
    if (!mapFeatures.find((f) => f.tempId === newFeature.tempId)) {
      setMapFeatures([...mapFeatures, newFeature]);
    }
  }
}
